import React, {
	useState,
	useEffect,
	useImperativeHandle,
	forwardRef,
	useRef,
	useMemo,
} from "react";
import {
	Download,
	AlertCircle,
	ChevronDown,
	ChevronUp,
	Edit2,
	MessageSquare,
	Send,
	X,
	Trash2,
	Info,
	Calendar,
	Flag,
	ChevronRight,
} from "lucide-react";
import {
	CircularProgress,
	Box,
	Typography,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	List,
	ListItem,
	ListItemText,
	IconButton,
	Tooltip,
	FormControl,
	Select as MuiSelect,
	MenuItem,
	OutlinedInput,
	DialogContentText,
} from "@mui/material";
import { apiClient } from "../../services/api";
import MilestoneDetailModal from "../Modals/MilestoneDetailModal";
import SerieDataTableSidePanel from "../Sidebars/SerieDataTableSidePanel";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import format from "date-fns/format";
import {
	cleanAndFormatComment,
	formatCommentForDisplay,
} from "../../utils/commentFormatter";
import { useUserAccess } from "../../hooks/useUserAccess";

// Custom Tooltip Component for Comments
const CommentTooltip = ({ children, commentText, user, title }) => {
	if (!commentText) {
		return children;
	}

	const formattedComment = formatCommentForDisplay(commentText);

	const tooltipContent = (
		<Box sx={{ maxWidth: 400, p: 1 }}>
			<Typography
				variant="caption"
				sx={{
					color: "#fff",
					fontWeight: 600,
					display: "block",
					mb: 1,
					borderBottom: "1px solid rgba(255,255,255,0.3)",
					pb: 0.5,
				}}
			>
				{title || "Latest Comment"}
			</Typography>

			{formattedComment.isMultiLine &&
			formattedComment.parsedComments.length > 0 ? (
				<Box>
					{formattedComment.parsedComments.slice(0, 3).map((comment, index) => (
						<Box
							key={index}
							sx={{
								mb: 1,
								pb: 1,
								borderBottom:
									index <
									Math.min(2, formattedComment.parsedComments.length - 1)
										? "1px solid rgba(255,255,255,0.2)"
										: "none",
							}}
						>
							{comment.date && comment.user && (
								<Typography
									variant="caption"
									sx={{
										color: "rgba(255,255,255,0.8)",
										fontSize: "10px",
										fontWeight: 500,
										display: "block",
										mb: 0.3,
									}}
								>
									{comment.date} - {comment.user}:
								</Typography>
							)}
							<Typography
								variant="body2"
								sx={{
									color: "#fff",
									fontSize: "12px",
									lineHeight: 1.4,
									whiteSpace: "pre-wrap",
								}}
							>
								{comment.text.length > 100
									? `${comment.text.substring(0, 100)}...`
									: comment.text}
							</Typography>
						</Box>
					))}
					{formattedComment.parsedComments.length > 3 && (
						<Typography
							variant="caption"
							sx={{
								color: "rgba(255,255,255,0.7)",
								fontSize: "11px",
								fontStyle: "italic",
								display: "block",
								mt: 1,
							}}
						>
							+{formattedComment.parsedComments.length - 3} more comments...
						</Typography>
					)}
				</Box>
			) : (
				<Box>
					{user && (
						<Typography
							variant="caption"
							sx={{
								color: "rgba(255,255,255,0.8)",
								fontSize: "11px",
								display: "block",
								mb: 0.5,
							}}
						>
							By: {user}
						</Typography>
					)}
					<Typography
						variant="body2"
						sx={{
							color: "#fff",
							fontSize: "12px",
							lineHeight: 1.4,
							whiteSpace: "pre-wrap",
						}}
					>
						{formattedComment.cleanText.length > 150
							? `${formattedComment.cleanText.substring(0, 150)}...`
							: formattedComment.cleanText}
					</Typography>
				</Box>
			)}

			<Typography
				variant="caption"
				sx={{
					color: "rgba(255,255,255,0.6)",
					fontSize: "10px",
					fontStyle: "italic",
					display: "block",
					mt: 1,
					borderTop: "1px solid rgba(255,255,255,0.3)",
					pt: 0.5,
				}}
			>
				Click to view all comments
			</Typography>
		</Box>
	);

	return (
		<Tooltip
			title={tooltipContent}
			arrow
			placement="top"
			componentsProps={{
				tooltip: {
					sx: {
						bgcolor: "rgba(30, 40, 50, 0.95)",
						backdropFilter: "blur(10px)",
						border: "1px solid rgba(255,255,255,0.1)",
						borderRadius: "8px",
						boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
						maxWidth: 450,
						"& .MuiTooltip-arrow": {
							color: "rgba(30, 40, 50, 0.95)",
						},
					},
				},
			}}
		>
			{children}
		</Tooltip>
	);
};

// ToggleSwitch Component - 3-state version (None → in Arbeit → erledigt → None)
const ToggleSwitch = ({ value, onChange, disabled, size = "small" }) => {
	const isSmall = size === "small";
	const switchWidth = isSmall ? 42 : 60;
	const switchHeight = isSmall ? 16 : 24;
	const circleSize = isSmall ? 12 : 20;
	const circleOffset = isSmall ? 2 : 2;

	const getStatusInfo = (statusValue) => {
		switch (statusValue) {
			case null:
			case undefined:
			case 0:
			case "":
				return {
					position: circleOffset,
					backgroundColor: "#e5e7eb",
					label: "None",
					textColor: "#6b7280",
				};
			case "in Arbeit":
			case 1:
				return {
					position: (switchWidth - circleSize) / 2,
					backgroundColor: "#f59e0b",
					label: "in Arbeit",
					textColor: "#d97706",
				};
			case "erledigt":
			case 2:
				return {
					position: switchWidth - circleSize - circleOffset,
					backgroundColor: "#10b981",
					label: "erledigt",
					textColor: "#059669",
				};
			default:
				return {
					position: circleOffset,
					backgroundColor: "#e5e7eb",
					label: "None",
					textColor: "#6b7280",
				};
		}
	};

	const statusInfo = getStatusInfo(value);
	return (
		<div
			onClick={!disabled ? onChange : undefined}
			style={{
				width: switchWidth,
				height: switchHeight,
				backgroundColor: statusInfo.backgroundColor,
				borderRadius: switchHeight / 2,
				position: "relative",
				cursor: disabled ? "not-allowed" : "pointer",
				transition: "all 0.2s ease",
				opacity: disabled ? 0.6 : 1,
				display: "inline-block",
				border: `1px solid ${statusInfo.textColor}`,
			}}
			title={`Status: ${statusInfo.label} (Click to cycle: None → in Arbeit → erledigt)`}
		>
			<div
				style={{
					width: circleSize,
					height: circleSize,
					backgroundColor: "white",
					borderRadius: "50%",
					position: "absolute",
					top: 1,
					left: statusInfo.position,
					transition: "all 0.2s ease",
					boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
					border: `1px solid ${statusInfo.textColor}`,
				}}
			/>
		</div>
	);
};

// Predefined Stock Status options
const STOCK_STATUS_OPTIONS = ["NOK", "NOK-A", "Vorg", "OK", "LAG", "NR"];

// Predefined BTT Status options (same as VFF)
const STATUS_BTT_OPTIONS = [
	"Request SF",
	"MET Late",
	"Risk PET",
	"Deleted",
	"AC",
];

// Stock Status dropdown component
const StockStatusSelect = ({
	value,
	onChange,
	disabled,
	plantKey,
	snr,
	project,
}) => {
	const [open, setOpen] = useState(false);

	const handleChange = (event) => {
		const newValue = event.target.value;
		console.log(`Stock status changed for ${plantKey}:`, newValue);
		if (onChange) {
			onChange(newValue, plantKey, snr, project);
		}
	};

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "4px",
				width: "100%",
			}}
		>
			{disabled && (
				<CircularProgress
					size={12}
					thickness={4}
					sx={{
						color: "#cc0000",
						flexShrink: 0,
					}}
				/>
			)}
			<FormControl size="small" fullWidth disabled={disabled}>
				<MuiSelect
					value={STOCK_STATUS_OPTIONS.includes(value) ? value : ""}
					onChange={handleChange}
					displayEmpty
					open={open}
					onOpen={() => setOpen(true)}
					onClose={() => setOpen(false)}
					input={<OutlinedInput />}
					sx={{
						fontSize: "10px",
						height: "18px",
						"& .MuiOutlinedInput-notchedOutline": {
							borderColor: "#ddd",
						},
						"& .MuiSelect-select": {
							padding: "2px 4px",
							fontSize: "10px",
							minHeight: "unset",
							fontFamily: "MAN EUROPE_Bold",
							textAlign: "center",
						},
						backgroundColor: "white",
						cursor: disabled ? "not-allowed" : "pointer",
						borderRadius: "2px",
						opacity: disabled ? 0.7 : 1,
					}}
					MenuProps={{
						PaperProps: {
							style: {
								maxHeight: 200,
								fontSize: "10px",
							},
						},
					}}
				>
					<MenuItem value="">
						<Typography
							sx={{ fontSize: "10px", fontFamily: "MAN EUROPE_Regular" }}
						>
							-
						</Typography>
					</MenuItem>
					{STOCK_STATUS_OPTIONS.map((option) => (
						<MenuItem key={option} value={option}>
							<Typography
								sx={{ fontSize: "10px", fontFamily: "MAN EUROPE_Regular" }}
							>
								{option}
							</Typography>
						</MenuItem>
					))}
				</MuiSelect>
			</FormControl>
		</div>
	);
};

// Helper function to format date for display
const formatDate = (dateString) => {
	try {
		if (!dateString) return "";
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return dateString;
		return date
			.toLocaleString("en-GB", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
				hour12: false,
			})
			.replace(",", "");
	} catch (e) {
		return dateString;
	}
};

// Series Comment Modal Component
const SeriesCommentModal = ({
	open,
	onClose,
	seriesId,
	snr,
	snrAnlageProjnr,
	onAddComment,
	newComment,
	setNewComment,
	editingCommentId,
	setEditingCommentId,
	editText,
	setEditText,
	getUserDisplayName,
	preloadedCommentData = null,
}) => {
	const [comments, setComments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [backgroundLoading, setBackgroundLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [hasInitialized, setHasInitialized] = useState(false);
	const [localComment, setLocalComment] = useState("");
	const currentUser = getUserDisplayName
		? getUserDisplayName()
		: "Current User";

	// Drag state
	const [position, setPosition] = useState({ x: null, y: null });
	const dragRef = useRef({
		dragging: false,
		startX: 0,
		startY: 0,
		originX: 0,
		originY: 0,
	});

	const handleDragStart = React.useCallback(
		(e) => {
			// Ignore clicks on interactive elements
			if (e.target.closest("button")) return;
			const paperEl =
				e.currentTarget
					.closest('[role="dialog"]')
					?.querySelector(".MuiDialog-paper") || e.currentTarget.parentElement;
			const rect = paperEl?.getBoundingClientRect() || { left: 0, top: 0 };
			dragRef.current = {
				dragging: true,
				startX: e.clientX,
				startY: e.clientY,
				originX: position.x !== null ? position.x : rect.left,
				originY: position.y !== null ? position.y : rect.top,
			};
			e.preventDefault();
		},
		[position],
	);

	useEffect(() => {
		const onMouseMove = (e) => {
			if (!dragRef.current.dragging) return;
			const dx = e.clientX - dragRef.current.startX;
			const dy = e.clientY - dragRef.current.startY;
			setPosition({
				x: dragRef.current.originX + dx,
				y: dragRef.current.originY + dy,
			});
		};
		const onMouseUp = () => {
			dragRef.current.dragging = false;
		};
		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
		return () => {
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
		};
	}, []);

	const handleCommentChange = React.useCallback((e) => {
		setLocalComment(e.target.value);
	}, []);
	useEffect(() => {
		if (open && snr && snrAnlageProjnr && !hasInitialized) {
			setHasInitialized(true);
			setLocalComment(newComment || "");

			// Check if we have preloaded comments
			if (
				preloadedCommentData?.hasComments &&
				preloadedCommentData?.latestComment
			) {
				console.log(
					"📄 Using preloaded series comment data for SNR:",
					snr,
					"Project:",
					snrAnlageProjnr,
				);
				const latestComment = preloadedCommentData.latestComment;
				setComments([latestComment]);
				setLoading(false);

				// Still fetch in background to ensure we have all comments
				setBackgroundLoading(true);
				fetchComments(true);
			} else if (preloadedCommentData?.hasComments === false) {
				// We know there are no comments, so show empty state immediately
				console.log(
					"📄 No comments found for SNR:",
					snr,
					"Project:",
					snrAnlageProjnr,
				);
				setComments([]);
				setLoading(false);
			} else {
				// No preloaded data, fetch normally
				fetchComments();
			}
		}
		if (!open) {
			setHasInitialized(false);
			setLocalComment("");
			setPosition({ x: null, y: null });
			if (setEditingCommentId && setEditText) {
				setEditingCommentId(null);
				setEditText("");
			}
		}
	}, [open, snr, snrAnlageProjnr]);
	const fetchComments = async (isBackgroundFetch = false) => {
		try {
			if (!isBackgroundFetch) {
				setLoading(true);
			}
			console.log(
				"🔍 Fetching series comments for SNR:",
				snr,
				"Project:",
				snrAnlageProjnr,
			);

			const response = await apiClient.get("/api/series-comments", {
				params: {
					snr: snr,
					snr_anlage_projnr: snrAnlageProjnr,
				},
			});

			console.log(
				"✅ Series comments fetched successfully:",
				response.data.comments?.length || 0,
				"comments",
			);
			setComments(response.data.comments || []);
		} catch (error) {
			console.error("❌ Error fetching series comments:", error);
			setComments([]);
		} finally {
			if (isBackgroundFetch) {
				setBackgroundLoading(false);
			} else {
				setLoading(false);
			}
		}
	};
	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!localComment.trim()) return;
		setSaving(true);

		const optimisticComment = {
			id: `temp_${Date.now()}`,
			text: localComment,
			user: currentUser,
			timestamp: new Date().toISOString(),
			last_modified: new Date().toISOString(),
			snr: snr,
			snr_anlage_projnr: snrAnlageProjnr,
			is_optimistic: true,
		};

		setComments((prevComments) => [optimisticComment, ...prevComments]);
		const commentTextToSave = localComment;
		setLocalComment("");
		if (setNewComment) setNewComment("");

		try {
			console.log(
				"📝 Adding series comment for SNR:",
				snr,
				"Project:",
				snrAnlageProjnr,
			);

			const response = await apiClient.post("/api/series-comments", {
				snr: snr,
				snr_anlage_projnr: snrAnlageProjnr,
				text: commentTextToSave,
				user: currentUser,
			});
			if (response.data && response.data.success) {
				console.log("✅ Series comment added successfully");

				await fetchComments();

				// Pass latest comment details back so the icon updates immediately
				if (onAddComment)
					onAddComment(
						commentTextToSave,
						currentUser,
						new Date().toISOString(),
					);
			} else {
				throw new Error(response.data?.message || "Failed to save comment");
			}
		} catch (error) {
			console.error("❌ Error adding series comment:", error);
			setComments((prevComments) =>
				prevComments.filter((comment) => comment.id !== optimisticComment.id),
			);

			setLocalComment(commentTextToSave);
			if (setNewComment) setNewComment(commentTextToSave);

			alert(
				`Error adding comment: ${error.response?.data?.detail || error.message || "Please try again."}`,
			);
		} finally {
			setSaving(false);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	};
	const handleUpdateComment = async (commentId) => {
		if (!editText.trim()) return;
		setSaving(true);

		try {
			console.log(
				"📝 Updating series comment:",
				commentId,
				"for SNR:",
				snr,
				"Project:",
				snrAnlageProjnr,
			);

			const response = await apiClient.put(
				`/api/series-comments/${commentId}`,
				{
					snr: snr,
					snr_anlage_projnr: snrAnlageProjnr,
					text: editText,
					user: currentUser,
				},
			);

			if (response.data) {
				console.log("✅ Series comment updated successfully");
				if (setEditingCommentId) setEditingCommentId(null);
				if (setEditText) setEditText("");
				await fetchComments();
			} else {
				const errorText = await response.text();
				console.error(
					"❌ Error updating series comment:",
					response.status,
					errorText,
				);
			}
		} catch (error) {
			console.error("❌ Error updating series comment:", error);
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteComment = async (commentId) => {
		if (
			!window.confirm(
				"Are you sure you want to delete this comment? This action cannot be undone.",
			)
		) {
			return;
		}

		setSaving(true);

		try {
			console.log(
				"🗑️ Starting delete process for series comment:",
				commentId,
				"SNR:",
				snr,
				"Project:",
				snrAnlageProjnr,
			);

			let response;
			if (commentId === "latest") {
				const deleteUrl = `/api/series-comments/latest`;
				const deleteData = {
					snr: snr,
					snr_anlage_projnr: snrAnlageProjnr,
				};
				console.log("🗑️ Deleting latest series comment using /latest endpoint");
				console.log("📝 DELETE URL:", deleteUrl);
				console.log("📦 Request payload:", JSON.stringify(deleteData, null, 2));

				response = await apiClient.delete(deleteUrl, {
					data: deleteData,
				});
			} else {
				const deleteUrl = `/api/series-comments/${commentId}`;
				const deleteData = {
					snr: snr,
					snr_anlage_projnr: snrAnlageProjnr,
				};
				console.log(
					"🗑️ Deleting specific series comment using /{commentId} endpoint",
				);
				console.log("📝 DELETE URL:", deleteUrl);
				console.log("📦 Request payload:", JSON.stringify(deleteData, null, 2));

				response = await apiClient.delete(deleteUrl, {
					data: deleteData,
				});
			}

			console.log("📨 Response received:", {
				status: response.status,
				statusText: response.statusText,
				headers: response.headers,
				data: response.data,
			});
			if (response.data && response.data.success) {
				console.log(
					"✅ Series comment deleted successfully:",
					response.data.message,
				);
				await fetchComments();
				// After delete, pass remaining latest comment (first in updated list) or empty to clear icon
				const updatedComments = await apiClient
					.get("/api/series-comments", {
						params: { snr, snr_anlage_projnr: snrAnlageProjnr },
					})
					.then((r) => r.data.comments || [])
					.catch(() => []);
				const newest = updatedComments[0];
				if (onAddComment)
					onAddComment(
						newest?.text || "",
						newest?.user || "",
						newest?.timestamp || "",
					);
			} else {
				console.error("❌ Unexpected response format:", response);
				alert(
					"Error deleting comment: Unexpected response format. Please try again.",
				);
			}
		} catch (error) {
			console.error("❌ Error deleting series comment - Full error details:");
			console.error("Error message:", error.message);
			console.error("Error code:", error.code);
			console.error("Error config:", error.config);
			console.error("Error response:", error.response);

			if (error.response) {
				console.error("Response status:", error.response.status);
				console.error("Response data:", error.response.data);
				console.error("Response headers:", error.response.headers);
				alert(
					`Error deleting comment: ${error.response.status} - ${error.response.data?.detail || error.message}`,
				);
			} else if (error.request) {
				console.error("No response received:", error.request);
				alert(
					"Error deleting comment: No response from server. Please check your connection.",
				);
			} else {
				console.error("Request setup error:", error.message);
				alert(`Error deleting comment: ${error.message}`);
			}
		} finally {
			setSaving(false);
		}
	};
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				style: {
					borderRadius: "0px",
					boxShadow: "0 10px 40px rgba(30, 40, 50, 0.15)",
					overflow: "hidden",
					height: "70vh",
					maxHeight: "700px",
					...(position.x !== null
						? {
								position: "fixed",
								left: position.x,
								top: position.y,
								margin: 0,
								transform: "none",
							}
						: {}),
				},
			}}
		>
			<Box
				onMouseDown={handleDragStart}
				sx={{
					backgroundColor: "#1e2832",
					color: "white",
					px: 3,
					py: 2,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
					cursor: "grab",
					userSelect: "none",
					"&:active": { cursor: "grabbing" },
				}}
			>
				<Box display="flex" alignItems="center" gap={2}>
					<MessageSquare size={20} />
					<Typography
						variant="h6"
						sx={{
							fontWeight: 600,
							fontFamily: "MAN EUROPE_Bold",
							fontSize: "16px",
							letterSpacing: "0.5px",
						}}
					>
						Series Comments
					</Typography>
					<Typography
						variant="body2"
						sx={{
							color: "rgba(255, 255, 255, 0.7)",
							fontSize: "14px",
						}}
					>
						SNR: {snr} | Project: {snrAnlageProjnr}
					</Typography>
				</Box>
				<button
					onClick={onClose}
					style={{
						background: "none",
						border: "none",
						color: "white",
						cursor: "pointer",
						padding: "8px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: "18px",
						transition: "all 0.2s ease",
					}}
					onMouseOver={(e) => {
						e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
						e.target.style.transform = "scale(1.05)";
					}}
					onMouseOut={(e) => {
						e.target.style.backgroundColor = "transparent";
						e.target.style.transform = "scale(1)";
					}}
				>
					<X size={18} />
				</button>
			</Box>

			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100% - 70px)",
				}}
			>
				<Box
					sx={{
						flex: 1,
						overflowY: "auto",
						backgroundColor: "#f8f9fa",
						"&::-webkit-scrollbar": {
							width: "6px",
						},
						"&::-webkit-scrollbar-track": {
							background: "#f1f1f1",
						},
						"&::-webkit-scrollbar-thumb": {
							background: "#c1c1c1",
							borderRadius: "3px",
						},
						"&::-webkit-scrollbar-thumb:hover": {
							background: "#a8a8a8",
						},
					}}
				>
					{loading ? (
						<Box
							display="flex"
							justifyContent="center"
							alignItems="center"
							height="100%"
							py={4}
						>
							<CircularProgress sx={{ color: "#cc0000" }} size={32} />
							<Typography variant="body2" sx={{ ml: 2, color: "#666" }}>
								Loading comments...
							</Typography>
						</Box>
					) : comments.length > 0 ? (
						<Box sx={{ p: 2 }}>
							{comments.map((item, index) => (
								<Box
									key={item.id || `comment-${index}`}
									sx={{
										backgroundColor: item.is_optimistic ? "#f8f9ff" : "white",
										p: 2.5,
										mb: 1.5,
										border: item.is_optimistic
											? "1px solid #c7d2fe"
											: "1px solid #e1e5e9",
										boxShadow: item.is_optimistic
											? "0 1px 3px rgba(99, 102, 241, 0.1)"
											: "0 1px 3px rgba(0, 0, 0, 0.05)",
										transition: "all 0.2s ease",
										opacity: item.is_optimistic ? 0.8 : 1,
										position: "relative",
										"&:hover": {
											boxShadow: item.is_optimistic
												? "0 4px 12px rgba(99, 102, 241, 0.15)"
												: "0 4px 12px rgba(0, 0, 0, 0.1)",
											transform: "translateY(-1px)",
										},
										"&:last-child": {
											mb: 0,
										},
									}}
								>
									{editingCommentId === item.id ? (
										<Box>
											<TextField
												fullWidth
												multiline
												rows={2}
												value={editText}
												onChange={(e) => setEditText(e.target.value)}
												variant="outlined"
												size="small"
												autoFocus
												placeholder="Edit your comment..."
												sx={{
													"& .MuiOutlinedInput-root": {
														fontSize: "14px",
														"&:hover fieldset": { borderColor: "#1e2832" },
														"&.Mui-focused fieldset": {
															borderColor: "#1e2832",
														},
													},
												}}
											/>
											<Box
												display="flex"
												justifyContent="flex-end"
												gap={1}
												mt={1.5}
											>
												<Button
													size="small"
													onClick={() => {
														setEditingCommentId(null);
														setEditText("");
													}}
													disabled={saving}
													sx={{
														color: "#666",
														textTransform: "none",
														fontSize: "12px",
														minWidth: "60px",
													}}
												>
													Cancel
												</Button>
												<Button
													variant="contained"
													size="small"
													onClick={() => handleUpdateComment(item.id)}
													disabled={saving || !editText.trim()}
													sx={{
														backgroundColor: "#cc0000",
														textTransform: "none",
														fontSize: "12px",
														minWidth: "60px",
														"&:hover": { backgroundColor: "#b30000" },
													}}
												>
													{saving ? "Saving..." : "Save"}
												</Button>
											</Box>
										</Box>
									) : (
										<Box>
											<Box
												display="flex"
												justifyContent="space-between"
												alignItems="center"
												mb={1}
											>
												<Box display="flex" alignItems="center" gap={1}>
													<Box
														sx={{
															width: "32px",
															height: "32px",
															borderRadius: "50%",
															backgroundColor: "#cc0000",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															color: "white",
															fontSize: "12px",
															fontWeight: "bold",
														}}
													>
														{(item.user || "S").charAt(0).toUpperCase()}
													</Box>
													<Typography
														variant="subtitle2"
														sx={{
															fontWeight: 600,
															color: "#1e2832",
															fontSize: "13px",
														}}
													>
														{item.user || "System"}
													</Typography>
												</Box>
												<Box display="flex" alignItems="center" gap={1}>
													<Typography
														variant="caption"
														sx={{
															color: "#999",
															fontSize: "11px",
															fontWeight: "bold",
														}}
													>
														{formatDate(item.timestamp)}
													</Typography>
													{!item.is_legacy && (
														<>
															<button
																onClick={() => {
																	setEditingCommentId(item.id);
																	setEditText(item.text);
																}}
																style={{
																	background: "none",
																	border: "none",
																	color: "#999",
																	cursor: "pointer",
																	padding: "4px",
																	borderRadius: "4px",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	transition: "all 0.2s ease",
																}}
																onMouseOver={(e) => {
																	e.target.style.backgroundColor =
																		"rgba(204, 0, 0, 0.08)";
																	e.target.style.color = "#cc0000";
																}}
																onMouseOut={(e) => {
																	e.target.style.backgroundColor =
																		"transparent";
																	e.target.style.color = "#999";
																}}
																title="Edit comment"
															>
																<Edit2 size={14} />
															</button>
															<button
																onClick={() => handleDeleteComment(item.id)}
																disabled={saving}
																style={{
																	background: "none",
																	border: "none",
																	color: "#999",
																	cursor: saving ? "not-allowed" : "pointer",
																	padding: "4px",
																	borderRadius: "4px",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	transition: "all 0.2s ease",
																	marginLeft: "4px",
																	opacity: saving ? 0.5 : 1,
																}}
																onMouseOver={(e) => {
																	if (!saving) {
																		e.target.style.backgroundColor =
																			"rgba(220, 38, 38, 0.08)";
																		e.target.style.color = "#dc2626";
																	}
																}}
																onMouseOut={(e) => {
																	if (!saving) {
																		e.target.style.backgroundColor =
																			"transparent";
																		e.target.style.color = "#999";
																	}
																}}
																title="Delete comment"
															>
																<Trash2 size={14} />
															</button>
														</>
													)}
												</Box>
											</Box>{" "}
											<Typography
												variant="body2"
												sx={{
													whiteSpace: "pre-wrap",
													color: "#333",
													lineHeight: 1.6,
													fontSize: "13px",
													ml: 5,
												}}
											>
												{item.text}
											</Typography>
										</Box>
									)}
								</Box>
							))}

							{backgroundLoading && (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										py: 2,
										mt: 1,
									}}
								>
									<CircularProgress
										size={20}
										sx={{ color: "#cc0000", mr: 1 }}
									/>
									<Typography
										variant="caption"
										sx={{
											color: "#666",
											fontSize: "12px",
										}}
									>
										Loading more comments...
									</Typography>
								</Box>
							)}
						</Box>
					) : (
						<Box
							display="flex"
							flexDirection="column"
							alignItems="center"
							justifyContent="center"
							height="100%"
							sx={{ color: "#666", py: 4 }}
						>
							<MessageSquare size={40} color="#d1d5db" />
							<Typography
								variant="body1"
								sx={{
									mt: 2,
									fontWeight: 500,
									color: "#4b5563",
								}}
							>
								No comments yet
							</Typography>
							<Typography
								variant="body2"
								sx={{
									color: "#9ca3af",
									fontSize: "13px",
									textAlign: "center",
									mt: 0.5,
								}}
							>
								Start the conversation by adding the first comment
							</Typography>
						</Box>
					)}
				</Box>

				<Box
					component="form"
					onSubmit={handleSubmit}
					sx={{
						p: 2.5,
						borderTop: "1px solid #e1e5e9",
						backgroundColor: "white",
						boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.05)",
					}}
				>
					<TextField
						fullWidth
						variant="outlined"
						placeholder="Write a comment..."
						value={localComment}
						onChange={handleCommentChange}
						onKeyDown={handleKeyDown}
						multiline
						rows={2}
						disabled={saving}
						sx={{
							"& .MuiOutlinedInput-root": {
								fontSize: "14px",
								backgroundColor: "#fafafa",
								"&:hover": {
									backgroundColor: "#f5f5f5",
								},
								"&:hover fieldset": { borderColor: "#1e2832" },
								"&.Mui-focused": {
									backgroundColor: "white",
								},
								"&.Mui-focused fieldset": { borderColor: "#1e2832" },
							},
						}}
					/>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mt: 2,
						}}
					>
						<Typography
							variant="caption"
							sx={{
								color: "#9ca3af",
								fontSize: "11px",
								fontWeight: "bold",
							}}
						>
							Press Enter to Submit, Shift + Enter for a new line
						</Typography>
						<Box sx={{ display: "flex", gap: 1.5 }}>
							<Button
								variant="outlined"
								onClick={onClose}
								disabled={saving}
								sx={{
									textTransform: "none",
									color: "#6b7280",
									borderColor: "#d1d5db",
									fontSize: "13px",
									fontFamily: "MAN EUROPE_Bold",
									borderRadius: "0px",
									minWidth: "70px",
									"&:hover": {
										borderColor: "#1e2832",
										backgroundColor: "rgba(30, 40, 50, 0.04)",
									},
								}}
							>
								Close
							</Button>
							<Button
								type="submit"
								variant="contained"
								disabled={!localComment.trim() || saving}
								sx={{
									textTransform: "none",
									backgroundColor: "#cc0000",
									fontFamily: "MAN EUROPE_Bold",
									fontSize: "13px",
									borderRadius: "0px",
									minWidth: "80px",
									fontWeight: 600,
									"&:hover": { backgroundColor: "#b30000" },
									"&.Mui-disabled": {
										backgroundColor: "#e5e7eb",
										color: "#9ca3af",
									},
								}}
							>
								{saving ? "Adding..." : "Add Comment"}
							</Button>
						</Box>
					</Box>
				</Box>
			</Box>
		</Dialog>
	);
};

// Helper: map a status string to a fill color for the status boxes
const getStatusBoxColor = (statusValue) => {
	if (!statusValue) return "#e0e0e0";
	const v = statusValue.trim().toLowerCase();
	if (v === "fet") return "#1b5e20"; // dark green
	if (v === "entfall" || v === "sto") return "#c62828"; // red
	if (v === "emcos ii") return "#388e3c"; // green
	if (v === "emcos i" || v === "neu" || v === "reaktiviert") return "#f9a825"; // yellow
	return "#e0e0e0"; // default grey
};

// Two overlapping colored square boxes component
const StatusDoubleBox = ({ serieStatus, snrStatus }) => {
	const box1Color = getStatusBoxColor(serieStatus);
	const box2Color = getStatusBoxColor(snrStatus);
	const hasAny = serieStatus || snrStatus;
	if (!hasAny) return null;
	return (
		<div
			style={{
				position: "relative",
				width: "14px",
				height: "14px",
				flexShrink: 0,
			}}
		>
			{/* Box 1 — large, from serie_tracking.status */}
			<div
				title={serieStatus || "—"}
				style={{
					position: "absolute",
					bottom: 0,
					left: 0,
					width: "10px",
					height: "10px",
					backgroundColor: box1Color,
					border: "1px solid rgba(0,0,0,0.35)",
					borderRadius: "1px",
				}}
			/>
			{/* Box 2 — small (half size), top-right corner overlapping box 1 */}
			<div
				title={snrStatus || "—"}
				style={{
					position: "absolute",
					top: 0,
					right: 0,
					width: "7px",
					height: "7px",
					backgroundColor: box2Color,
					border: "1px solid rgba(0,0,0,0.35)",
					borderRadius: "1px",
				}}
			/>
		</div>
	);
};

const SerieTrackingDataTable = forwardRef((props, ref) => {
	const {
		onSelectItems,
		selectedItems = [],
		editOptions,
		columnVisibility,
		selectedViewType,
		onItemsPerPageChange,
		getUserDisplayName,
		skipInitialFetch = false,
	} = props;
	// Access control — get user email and check write access
	const userEmail = useMemo(() => {
		if (props.getUserEmail) {
			return props.getUserEmail();
		}
		// Fallback to localStorage
		const username = localStorage.getItem("username");
		if (username && username.includes("@")) {
			return username;
		}
		return "user@man.eu"; // Default fallback
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.getUserEmail]);

	const {
		canWrite,
		canEditProtected,
		isLoading: accessLoading,
		refreshAccess,
	} = useUserAccess(userEmail);

	// Access Denied Modal state
	const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(100);
	const [seriesData, setSeriesData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [error, setError] = useState(null);
	const [totalPages, setTotalPages] = useState(1);
	const [totalRecords, setTotalRecords] = useState(0);
	const [hasMoreData, setHasMoreData] = useState(true);
	const [activeFilters, setActiveFilters] = useState({});
	const [lastDbUpdate, setLastDbUpdate] = useState(null);
	const dbUpdateFetched = React.useRef(false);
	const isManualFetch = React.useRef(false);
	// When true, ALL automatic fetches triggered by the useEffect are suppressed.
	// Initialized from the skipInitialFetch prop so it is set before the first effect
	// runs (even under React StrictMode double-invocation).
	// Cleared inside fetchData() (useImperativeHandle) the moment the parent explicitly
	// supplies its own filters — after that, normal reactive behaviour resumes.
	const suppressAutoFetchRef = React.useRef(skipInitialFetch);

	// SNR sorting state - default to null (no sorting initially)
	const [snrSortOrder, setSnrSortOrder] = useState(null); // null (no sort), 'asc', or 'desc'
	// Selection states for checkboxes
	const [allSelected, setAllSelected] = useState(false);
	// Range selection popover state
	const [rangePopoverOpen, setRangePopoverOpen] = useState(false);
	const [rangeFrom, setRangeFrom] = useState("");
	const [rangeTo, setRangeTo] = useState("");
	const [rangeError, setRangeError] = useState("");
	const rangePopoverRef = useRef(null);
	const checkboxRef = useRef(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedRowData, setSelectedRowData] = useState(null);
	const [editingResponsible, setEditingResponsible] = useState({});
	const [editingFollowup, setEditingFollowup] = useState({});
	const [responsibleValues, setResponsibleValues] = useState({});
	const [followupValues, setFollowupValues] = useState({});
	const [updatingSeries, setUpdatingSeries] = useState({});
	const [isGlobalEditMode, setIsGlobalEditMode] = useState(false);
	const [isBulkSaving, setIsBulkSaving] = useState(false);
	const [openFollowupDatePicker, setOpenFollowupDatePicker] = useState(null);
	// Stock status update state
	const [updatingStockStatus, setUpdatingStockStatus] = useState({});
	// Status toggle update state
	const [updatingStatusToggle, setUpdatingStatusToggle] = useState({}); // BTT Status state management
	const [updatingBttStatus, setUpdatingBttStatus] = useState({});
	const [removingBttChip, setRemovingBttChip] = useState({}); // { [itemKey]: chipVal } — tracks which chip is being removed
	const [tempBttStatusSelections, setTempBttStatusSelections] = useState({}); // Flagging state management
	const [updatingFlagging, setUpdatingFlagging] = useState({});
	// Edit-mode flagging values — keyed by `${snr}_${project}`, value is 'Y' or 'N'
	const [flaggingValues, setFlaggingValues] = useState({});
	const [commentModalOpen, setCommentModalOpen] = useState(false);
	const [selectedCommentSeries, setSelectedCommentSeries] = useState(null);
	const [newComment, setNewComment] = useState("");
	const [editingCommentId, setEditingCommentId] = useState(null);
	const [editText, setEditText] = useState(""); // Comments data — populated inline from the main series-data response (same pattern as VFF)
	const [commentsData, setCommentsData] = useState({});
	// Side panel state
	const [sidePanelOpen, setSidePanelOpen] = useState(false);
	const [selectedSidePanelRow, setSelectedSidePanelRow] = useState(null); // SNR Detail modal state
	const [snrDetailModalOpen, setSnrDetailModalOpen] = useState(false);
	const [snrDetailData, setSnrDetailData] = useState(null);
	const [snrDetailLoading, setSnrDetailLoading] = useState(false);
	const [snrDetailSnr, setSnrDetailSnr] = useState(null);
	// Pre-fetched SNR detail map — populated inline from series-data response (same pattern as commentsData)
	const [snrDetailsData, setSnrDetailsData] = useState({}); // Concurrent update prevention modal — blocks simultaneous flagging / BTT status / status toggle requests
	// blockedBy: 'flagging' | 'btt' | 'status' — which operation type is currently running
	const [flagUpdateModal, setFlagUpdateModal] = useState({
		open: false,
		message: "",
		blockedBy: null,
	});
	useImperativeHandle(ref, () => ({
		fetchData: (
			newFilters = {},
			page = 1,
			itemsPerPageParam = itemsPerPage,
			viewTypeOrIsLoadMore = false,
			isLoadMore = false,
			sortOrder = snrSortOrder,
		) => {
			// Handle the old signature where view was a string or new signature where it's a boolean
			if (typeof viewTypeOrIsLoadMore === "boolean") {
				isLoadMore = viewTypeOrIsLoadMore;
			}

			if (!isLoadMore) {
				setSeriesData([]);
				setLoading(true);
				setError(null);
			}

			// Allow the useEffect to react to future filter/page changes again now that
			// the parent has explicitly called fetchData with its own filters.
			suppressAutoFetchRef.current = false;
			isManualFetch.current = true;

			setActiveFilters(newFilters);
			setCurrentPage(page);
			if (itemsPerPageParam !== itemsPerPage && !isLoadMore) {
				setItemsPerPage(itemsPerPageParam);
			}

			// Return the promise so callers can await a full refresh
			return fetchSeriesData(
				newFilters,
				page,
				itemsPerPageParam,
				isLoadMore,
				sortOrder,
			);
		},
		getAllData: () => seriesData,
		loadMoreData: () => {
			if (isLoadingMore || !hasMoreData || loading) return;

			const nextPage = currentPage + 1;
			setCurrentPage(nextPage);
			fetchSeriesData(
				activeFilters,
				nextPage,
				itemsPerPage,
				true,
				snrSortOrder,
			);
		}, // Reset sort order (called when clearing filters)
		resetSort: () => {
			setSnrSortOrder(null);
		},
		/**
		 * Patch specific rows in-place after a bulk edit, without resetting scroll position.
		 * @param {Array<{snr: string, snr_anlage_projnr: string, [field]: any}>} patches
		 *   Each element must have `snr` and `snr_anlage_projnr` as keys, plus any raw-data
		 *   fields that changed (e.g. flagging, status_toggle, btt_status,
		 *   atr_verantwortlich, atr_wiedervorlage, latest_comment).
		 */
		patchRows: (patches) => {
			if (!patches || patches.length === 0) return;
			const patchMap = new Map(
				patches.map((p) => [`${p.snr}_${p.snr_anlage_projnr}`, p]),
			);
			setSeriesData((prev) =>
				prev.map((item) => {
					const key = `${item.snr}_${item.snr_anlage_projnr}`;
					const patch = patchMap.get(key);
					if (!patch) return item;
					// Spread only the fields that were supplied in the patch
					const updated = { ...item };
					if ("flagging" in patch) updated.flagging = patch.flagging;
					if ("status_toggle" in patch)
						updated.status_toggle = patch.status_toggle;
					if ("btt_status" in patch) updated.btt_status = patch.btt_status;
					if ("atr_verantwortlich" in patch)
						updated.atr_verantwortlich = patch.atr_verantwortlich;
					if ("atr_wiedervorlage" in patch)
						updated.atr_wiedervorlage = patch.atr_wiedervorlage;
					if ("latest_comment" in patch)
						updated.latest_comment = patch.latest_comment;
					if ("tags" in patch) updated.tags = patch.tags;
					return updated;
				}),
			);
			// Also update commentsData if a comment was included in any patch
			const commentPatches = patches.filter((p) => "latest_comment" in p);
			if (commentPatches.length > 0) {
				setCommentsData((prev) => {
					const next = { ...prev };
					commentPatches.forEach((p) => {
						const key = `${p.snr}_${p.snr_anlage_projnr}`;
						next[key] = {
							...prev[key],
							latest_comment:
								p.latest_comment || prev[key]?.latest_comment || "",
							latest_comment_user:
								p.latest_comment_user || prev[key]?.latest_comment_user || "",
							latest_comment_date:
								p.latest_comment_date || prev[key]?.latest_comment_date || "",
						};
					});
					return next;
				});
			}
		},
		// Expose edit functions to parent
		enableEditMode: handleEnableEditMode,
		cancelEditMode: handleCancelEditMode,
		bulkSave: handleBulkSave,
		isInEditMode: () => isGlobalEditMode,
		isSaving: () => isBulkSaving,
	}));
	const handleScroll = React.useCallback(
		(e) => {
			if (isLoadingMore || !hasMoreData || loading) return;

			const container = e.target;
			const scrollTop = container.scrollTop;
			const scrollHeight = container.scrollHeight;
			const clientHeight = container.clientHeight;

			const threshold = 300;
			const remainingScroll = scrollHeight - scrollTop - clientHeight;

			if (remainingScroll < threshold) {
				console.log("🔄 Infinite scroll triggered - Loading more data...", {
					scrollTop,
					scrollHeight,
					clientHeight,
					remaining: remainingScroll,
					threshold,
					currentPage,
					hasMoreData,
				});
				const nextPage = currentPage + 1;
				setCurrentPage(nextPage);
				fetchSeriesData(
					activeFilters,
					nextPage,
					itemsPerPage,
					true,
					snrSortOrder,
				);
			}
		},
		[
			isLoadingMore,
			hasMoreData,
			loading,
			currentPage,
			activeFilters,
			itemsPerPage,
			snrSortOrder,
		],
	);
	// Handle select-all checkbox click — open range popover instead of immediately selecting
	const handleSelectAllClick = (e) => {
		e.preventDefault();
		setRangeFrom("");
		setRangeTo("");
		setRangeError("");
		setRangePopoverOpen((prev) => !prev);
	};

	// Apply range selection
	const handleApplyRangeSelection = () => {
		const from = parseInt(rangeFrom, 10);
		const to = parseInt(rangeTo, 10);
		const total = seriesData.length;

		if (!rangeFrom && !rangeTo) {
			// No range — select all loaded items
			const pageData = seriesData.map((item) => ({
				...item,
				id: `${item.snr}_${item.snr_anlage_projnr}`,
				kast_key: `${item.snr}_${item.snr_anlage_projnr}`,
				vehicle_no: item.snr,
				part_no: item.snr,
				part_description: item.bend,
				supplier: item.lieferant,
				required_date: item.met,
			}));
			onSelectItems?.(pageData);
			setAllSelected(true);
			setRangePopoverOpen(false);
			return;
		}

		if (isNaN(from) || isNaN(to)) {
			setRangeError("Please enter valid numbers for both fields.");
			return;
		}
		if (from < 1 || to < 1) {
			setRangeError("Row numbers must be 1 or greater.");
			return;
		}
		if (from > to) {
			setRangeError('"From" must be less than or equal to "To".');
			return;
		}
		if (from > total) {
			setRangeError(`"From" (${from}) exceeds total loaded rows (${total}).`);
			return;
		}

		const clampedTo = Math.min(to, total);
		const rangeItems = seriesData.slice(from - 1, clampedTo).map((item) => ({
			...item,
			id: `${item.snr}_${item.snr_anlage_projnr}`,
			kast_key: `${item.snr}_${item.snr_anlage_projnr}`,
			vehicle_no: item.snr,
			part_no: item.snr,
			part_description: item.bend,
			supplier: item.lieferant,
			required_date: item.met,
		}));

		// Merge with existing selections, no duplicates
		const existingIds = new Set(selectedItems.map((s) => s.id || s.kast_key));
		const newItems = rangeItems.filter((item) => !existingIds.has(item.id));
		const merged = [...selectedItems, ...newItems];

		onSelectItems?.(merged);
		setAllSelected(merged.length === total && total > 0);
		setRangeError("");
		setRangePopoverOpen(false);
	};

	// Close popover when clicking outside
	useEffect(() => {
		if (!rangePopoverOpen) return;
		const handleOutsideClick = (e) => {
			if (
				rangePopoverRef.current &&
				!rangePopoverRef.current.contains(e.target) &&
				checkboxRef.current &&
				!checkboxRef.current.contains(e.target)
			) {
				setRangePopoverOpen(false);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, [rangePopoverOpen]);

	// Handle Deselect All button
	const handleDeselectAll = () => {
		onSelectItems?.([]);
		setAllSelected(false);
	};
	// Handle individual row checkbox
	const handleSelectItem = (row) => {
		// Handle both raw data (from seriesData) and processed data (from currentData)
		const snr = row.SNR || row.snr;
		const project = row.project || row.snr_anlage_projnr;
		const itemId = `${snr}_${project}`;

		const isSelected = selectedItems.some(
			(selected) => selected.id === itemId || selected.kast_key === itemId,
		);

		let newSelectedItems;
		if (isSelected) {
			// Remove from selection
			newSelectedItems = selectedItems.filter(
				(selected) => selected.id !== itemId && selected.kast_key !== itemId,
			);
		} else {
			// Add to selection - use the correct field names
			const selectedItem = {
				...row,  // spread all raw row data so bulk edit modal has everything
				id: itemId,
				kast_key: itemId,
				vehicle_no: snr,
				part_no: snr,
				part_description: row.description || row.bend,
				supplier: row.supplier || row.lieferant,
				required_date: row.met,
				// Explicit aliases for bulk edit modal display
				SNR: snr,
				project: project,
				alg: row.alg,
			};
			newSelectedItems = [...selectedItems, selectedItem];
		}

		onSelectItems?.(newSelectedItems);

		// Update allSelected state based on whether all current page items are selected
		setAllSelected(
			newSelectedItems.length === seriesData.length && seriesData.length > 0,
		);
	};

	const formatDateForBackend = (date) => {
		if (!date) return null;
		try {
			// Ensure it's a valid Date object
			if (!(date instanceof Date) || isNaN(date.getTime())) {
				console.warn("Invalid date passed to formatDateForBackend:", date);
				return null;
			}
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const day = String(date.getDate()).padStart(2, "0");
			return `${year}-${month}-${day}`;
		} catch (e) {
			console.warn("Error formatting date for backend:", date, e);
			return null;
		}
	};
	const handleSaveResponsibleField = async (snr, snrAnlageProjnr) => {
		// Check write access first
		if (!canWrite) {
			setShowAccessDeniedModal(true);
			console.warn(
				"🚫 User attempted to update responsible without write access:",
				userEmail,
			);
			return;
		}

		try {
			const itemId = `${snr}_${snrAnlageProjnr}`;
			const newValue = responsibleValues[itemId];

			if (!newValue) return;

			console.log("Updating responsible for series:", {
				snr,
				snrAnlageProjnr,
				newValue,
			});

			setUpdatingSeries((prev) => ({
				...prev,
				[`${itemId}_responsible`]: true,
			}));

			try {
				const response = await apiClient.put("/api/update-series-responsible", {
					snr: snr,
					snr_anlage_projnr: snrAnlageProjnr,
					value: newValue,
				});

				console.log("✅ Responsible updated successfully:", response.data);
				setSeriesData((prev) =>
					prev.map((item) => {
						if (item.SNR === snr && item.project === snrAnlageProjnr) {
							return {
								...item,
								status: {
									...item.status,
									responsible: newValue,
								},
							};
						}
						return item;
					}),
				);

				setEditingResponsible((prev) => ({
					...prev,
					[itemId]: false,
				}));
			} catch (error) {
				console.error("❌ Error updating responsible:", error);
				throw error;
			}
		} catch (error) {
			console.error("Error saving responsible field:", error);

			// 403 = access denied — show the modal instead of alert
			if (error.response?.status === 403) {
				refreshAccess();
				setShowAccessDeniedModal(true);
				return;
			}

			let userMessage = `Failed to update responsible field. Please try again.`;
			if (error.response?.data?.detail) {
				userMessage = error.response.data.detail;
			} else if (error.message && error.message !== "Network Error") {
				userMessage = error.message;
			}

			alert(userMessage);
		} finally {
			const itemId = `${snr}_${snrAnlageProjnr}`;
			setUpdatingSeries((prev) => ({
				...prev,
				[`${itemId}_responsible`]: false,
			}));
		}
	};
	const handleSaveFollowupField = async (snr, snrAnlageProjnr) => {
		// Check write access first
		if (!canWrite) {
			setShowAccessDeniedModal(true);
			console.warn(
				"🚫 User attempted to update followup without write access:",
				userEmail,
			);
			return;
		}

		try {
			const itemId = `${snr}_${snrAnlageProjnr}`;
			const newValue = followupValues[itemId];

			console.log("Updating followup date for series:", {
				snr,
				snrAnlageProjnr,
				newValue,
			});

			setUpdatingSeries((prev) => ({
				...prev,
				[`${itemId}_followup`]: true,
			}));

			try {
				let formattedValue = null;
				if (newValue) {
					formattedValue = formatDateForBackend(newValue);
				}

				const response = await apiClient.put("/api/update-series-followup", {
					snr: snr,
					snr_anlage_projnr: snrAnlageProjnr,
					value: formattedValue,
				});

				console.log("✅ Followup date updated successfully:", response.data);
				setSeriesData((prev) =>
					prev.map((item) => {
						if (item.SNR === snr && item.project === snrAnlageProjnr) {
							return {
								...item,
								status: {
									...item.status,
									followUp: formattedValue
										? format(newValue, "dd/MM/yyyy")
										: "-",
								},
							};
						}
						return item;
					}),
				);

				setEditingFollowup((prev) => ({
					...prev,
					[itemId]: false,
				}));
			} catch (error) {
				console.error("❌ Error updating followup date:", error);
				throw error;
			}
		} catch (error) {
			console.error("Error saving followup field:", error);

			// 403 = access denied — show the modal instead of alert
			if (error.response?.status === 403) {
				refreshAccess();
				setShowAccessDeniedModal(true);
				return;
			}

			let userMessage = `Failed to update followup date. Please try again.`;
			if (error.response?.data?.detail) {
				userMessage = error.response.data.detail;
			} else if (error.message && error.message !== "Network Error") {
				userMessage = error.message;
			}

			alert(userMessage);
		} finally {
			const itemId = `${snr}_${snrAnlageProjnr}`;
			setUpdatingSeries((prev) => ({
				...prev,
				[`${itemId}_followup`]: false,
			}));
		}
	};
	// Stock status update function
	const handleUpdateStockStatus = async (
		newValue,
		plantKey,
		snr,
		snrAnlageProjnr,
	) => {
		// Check write access first
		if (!canWrite) {
			setShowAccessDeniedModal(true);
			console.warn(
				"🚫 User attempted to update stock status without write access:",
				userEmail,
			);
			return;
		}

		try {
			const updateKey = `${snr}_${snrAnlageProjnr}_${plantKey}`;

			console.log("Updating stock status:", {
				snr,
				snrAnlageProjnr,
				plantKey,
				newValue,
			});

			setUpdatingStockStatus((prev) => ({
				...prev,
				[updateKey]: true,
			})); // Map frontend plant keys to backend column names
			const plantColumnMapping = {
				M: "lieferfaeh_m_status",
				K: "lieferfaeh_c_status",
				B: "lieferfaeh_y_status",
				S: "lieferfaeh_w_status",
			};

			const columnName = plantColumnMapping[plantKey];
			if (!columnName) {
				throw new Error(`Unknown plant key: ${plantKey}`);
			}

			const response = await apiClient.put("/api/update-series-stock-status", {
				snr: snr,
				snr_anlage_projnr: snrAnlageProjnr,
				plant: plantKey,
				column: columnName,
				value: newValue,
			});

			console.log("✅ Stock status updated successfully:", response.data);
			// Update local state
			setSeriesData((prev) =>
				prev.map((item) => {
					if (item.snr === snr && item.snr_anlage_projnr === snrAnlageProjnr) {
						return {
							...item,
							stock_status: {
								...item.stock_status,
								[plantKey]: {
									...item.stock_status[plantKey],
									status: newValue,
								},
							},
						};
					}
					return item;
				}),
			);
		} catch (error) {
			console.error("❌ Error updating stock status:", error);

			// 403 = access denied — show the modal instead of alert
			if (error.response?.status === 403) {
				refreshAccess();
				setShowAccessDeniedModal(true);
				return;
			}

			let userMessage = `Failed to update stock status. Please try again.`;
			if (error.response?.data?.detail) {
				userMessage = error.response.data.detail;
			} else if (error.message && error.message !== "Network Error") {
				userMessage = error.message;
			}

			alert(userMessage);
		} finally {
			const updateKey = `${snr}_${snrAnlageProjnr}_${plantKey}`;
			setUpdatingStockStatus((prev) => ({
				...prev,
				[updateKey]: false,
			}));
		}
	}; // BTT Status update handler
	const handleBttStatusUpdate = async (
		snr,
		snrAnlageProjnr,
		selectedValues,
	) => {
		if (!canWrite) {
			setShowAccessDeniedModal(true);
			return;
		}
		// Block if a flagging update or status toggle is already running
		if (Object.keys(updatingFlagging).length > 0) {
			setFlagUpdateModal({
				open: true,
				blockedBy: "flagging",
				message:
					"A flag update is already in progress. Please wait for it to complete before changing the BTT status.",
			});
			return;
		}
		if (Object.keys(updatingStatusToggle).length > 0) {
			setFlagUpdateModal({
				open: true,
				blockedBy: "status",
				message:
					"A status update is already in progress. Please wait for it to complete before changing the BTT status.",
			});
			return;
		}
		const itemKey = `${snr}_${snrAnlageProjnr}`;
		try {
			setUpdatingBttStatus((prev) => ({ ...prev, [itemKey]: true }));
			const newValue = Array.isArray(selectedValues)
				? selectedValues.join(",")
				: selectedValues || "";
			const userName = userEmail || "Unknown";
			const response = await apiClient.put(
				"/api/update-series-item",
				{
					snr,
					snr_anlage_projnr: snrAnlageProjnr,
					field: "btt_status",
					value: newValue || null,
				},
				{ headers: { "X-User-Name": userName } },
			);
			if (response.data) {
				setSeriesData((prev) =>
					prev.map((item) => {
						if (
							item.snr === snr &&
							item.snr_anlage_projnr === snrAnlageProjnr
						) {
							return { ...item, btt_status: newValue };
						}
						return item;
					}),
				);
				console.log(`✅ BTT Status updated for ${snr}/${snrAnlageProjnr}`);
			}
		} catch (error) {
			console.error("❌ Error updating BTT status:", error);
			if (error.response?.status === 403) {
				refreshAccess();
				setShowAccessDeniedModal(true);
			} else {
				alert("Error updating BTT Status. Please try again.");
			}
		} finally {
			setUpdatingBttStatus((prev) => {
				const s = { ...prev };
				delete s[itemKey];
				return s;
			});
			setRemovingBttChip((prev) => {
				const s = { ...prev };
				delete s[itemKey];
				return s;
			});
		}
	}; // Flagging toggle handler
	const handleFlaggingToggle = async (snr, snrAnlageProjnr, currentValue) => {
		if (!canWrite) {
			setShowAccessDeniedModal(true);
			return;
		}
		// Block if any concurrent update is already running — show warning modal instead
		if (Object.keys(updatingFlagging).length > 0) {
			console.log(
				"🚫 Preventing simultaneous flag updates. Current operations:",
				Object.keys(updatingFlagging),
			);
			setFlagUpdateModal({
				open: true,
				blockedBy: "flagging",
				message:
					"A flag update is already in progress. Please wait for it to complete before flagging another item.",
			});
			return;
		}
		if (Object.keys(updatingBttStatus).length > 0) {
			setFlagUpdateModal({
				open: true,
				blockedBy: "btt",
				message:
					"A BTT status update is already in progress. Please wait for it to complete before changing the flag.",
			});
			return;
		}
		if (Object.keys(updatingStatusToggle).length > 0) {
			setFlagUpdateModal({
				open: true,
				blockedBy: "status",
				message:
					"A status update is already in progress. Please wait for it to complete before changing the flag.",
			});
			return;
		}
		const itemKey = `${snr}_${snrAnlageProjnr}`;
		const newValue = currentValue === "Y" ? "N" : "Y";
		// Optimistic update
		setSeriesData((prev) =>
			prev.map((item) => {
				if (item.snr === snr && item.snr_anlage_projnr === snrAnlageProjnr) {
					return { ...item, flagging: newValue };
				}
				return item;
			}),
		);
		try {
			setUpdatingFlagging((prev) => ({ ...prev, [itemKey]: true }));
			const userName = userEmail || "Unknown";
			const response = await apiClient.post(
				"/api/update-series-flagging",
				{
					snr,
					snr_anlage_projnr: snrAnlageProjnr,
					value: newValue,
				},
				{ headers: { "X-User-Name": userName } },
			);
			if (response.data?.success) {
				console.log(
					`✅ Series flagging updated for ${snr}/${snrAnlageProjnr} → ${newValue}`,
				);
			}
		} catch (error) {
			console.error("❌ Error updating series flagging:", error);
			// Revert on error
			setSeriesData((prev) =>
				prev.map((item) => {
					if (item.snr === snr && item.snr_anlage_projnr === snrAnlageProjnr) {
						return { ...item, flagging: currentValue };
					}
					return item;
				}),
			);
			if (error.response?.status === 403) {
				refreshAccess();
				setShowAccessDeniedModal(true);
			} else {
				alert("Error updating flag status. Please try again.");
			}
		} finally {
			setUpdatingFlagging((prev) => {
				const s = { ...prev };
				delete s[itemKey];
				return s;
			});
		}
	};
	// Status toggle handler (status_toggle field)- 3-step cycle: null → "in Arbeit" → "erledigt" → null
	const handleStatusToggle = async (snr, snrAnlageProjnr, currentValue) => {
		if (!canWrite) {
			setShowAccessDeniedModal(true);
			return;
		}
		// Block if a flagging or BTT status update is already running
		if (Object.keys(updatingFlagging).length > 0) {
			setFlagUpdateModal({
				open: true,
				blockedBy: "flagging",
				message:
					"A flag update is already in progress. Please wait for it to complete before changing the status.",
			});
			return;
		}
		if (Object.keys(updatingBttStatus).length > 0) {
			setFlagUpdateModal({
				open: true,
				blockedBy: "btt",
				message:
					"A BTT status update is already in progress. Please wait for it to complete before changing the status.",
			});
			return;
		}

		// Determine the next value in the cycle
		let newValue;
		switch (currentValue) {
			case null:
			case undefined:
			case 0:
			case "":
				newValue = "in Arbeit";
				break;
			case "in Arbeit":
			case 1:
				newValue = "erledigt";
				break;
			case "erledigt":
			case 2:
				newValue = null;
				break;
			default:
				newValue = "in Arbeit";
		}

		const toggleKey = `${snr}_${snrAnlageProjnr}`;
		console.log(
			`🔄 Series status toggle for ${toggleKey}: ${currentValue} → ${newValue}`,
		);

		// Optimistic local update
		setSeriesData((prev) =>
			prev.map((item) => {
				if (item.snr === snr && item.snr_anlage_projnr === snrAnlageProjnr) {
					return { ...item, status_toggle: newValue };
				}
				return item;
			}),
		);

		setUpdatingStatusToggle((prev) => ({ ...prev, [toggleKey]: true }));

		try {
			await apiClient.put("/api/update-series-item", {
				snr,
				snr_anlage_projnr: snrAnlageProjnr,
				field: "status_toggle",
				value: newValue,
			});
			console.log(
				`✅ Series status toggle updated successfully for ${toggleKey}`,
			);
		} catch (error) {
			console.error("❌ Error updating series status toggle:", error);

			// Revert optimistic update on failure
			setSeriesData((prev) =>
				prev.map((item) => {
					if (item.snr === snr && item.snr_anlage_projnr === snrAnlageProjnr) {
						return { ...item, status_toggle: currentValue };
					}
					return item;
				}),
			);
			if (error.response?.status === 403) {
				refreshAccess();
				setShowAccessDeniedModal(true);
				return;
			}

			const userMessage =
				error.response?.data?.detail ||
				"Failed to update status. Please try again.";
			alert(userMessage);
		} finally {
			setUpdatingStatusToggle((prev) => {
				const s = { ...prev };
				delete s[toggleKey];
				return s;
			});
		}
	};

	const handleSnrSort = () => {
		// Cycle through: null -> asc -> desc -> asc (first click starts sorting)
		let newSortOrder;
		if (snrSortOrder === null) {
			newSortOrder = "asc";
		} else if (snrSortOrder === "asc") {
			newSortOrder = "desc";
		} else {
			newSortOrder = "asc";
		}

		console.log("🔄 SNR Sort order changed:", snrSortOrder, "->", newSortOrder);

		// Set the manual fetch flag to prevent useEffect from triggering
		isManualFetch.current = true;

		setSnrSortOrder(newSortOrder);

		// Reset to page 1 and fetch data with new sort order
		setCurrentPage(1);
		setSeriesData([]);
		fetchSeriesData(activeFilters, 1, itemsPerPage, false, newSortOrder);
	};

	const fetchLastDbUpdate = async () => {
		if (dbUpdateFetched.current) return;
		dbUpdateFetched.current = true;
		try {
			const response = await apiClient.get("/api/series-last-db-update");
			if (response.data && response.data.last_update) {
				setLastDbUpdate(response.data.last_update);
			}
		} catch (error) {
			console.error("❌ Error fetching series last DB update:", error);
			dbUpdateFetched.current = false;
		}
	};

	useEffect(() => {
		fetchLastDbUpdate();
	}, []);

	const fetchSeriesData = async (
		filterParams = {},
		page = currentPage,
		items = itemsPerPage,
		isLoadMore = false,
		sortOrder = snrSortOrder,
	) => {
		try {
			if (!isLoadMore) {
				setLoading(true);
				setError(null);
			} else {
				setIsLoadingMore(true);
			}

			console.log("🔍 SerieTrackingDataTable: fetchSeriesData called with:", {
				filterParams,
				page,
				items,
				isLoadMore,
				sortOrder,
			});

			const currentFilters = filterParams;

			if (!isLoadMore) {
				setActiveFilters(currentFilters);
			}

			const requestParams = {
				page: page,
				items_per_page: items,
			};

			let response;
			const hasFilters =
				(currentFilters.projectNo && currentFilters.projectNo.length > 0) ||
				(currentFilters.alg && currentFilters.alg.length > 0) ||
				(currentFilters.programm && currentFilters.programm.length > 0) ||
				(currentFilters.changeNo && currentFilters.changeNo.length > 0) ||
				(currentFilters.responsible && currentFilters.responsible.length > 0) ||
				(currentFilters.seTeam && currentFilters.seTeam.length > 0) ||
				currentFilters.followupDateFrom ||
				currentFilters.followupDateTo ||
				(currentFilters.bza && currentFilters.bza.length > 0) ||
				(currentFilters.a80Status && currentFilters.a80Status.length > 0) ||
				(currentFilters.e99Status && currentFilters.e99Status.length > 0) ||
				(currentFilters.p99Status && currentFilters.p99Status.length > 0) ||
				(currentFilters.qi000Status && currentFilters.qi000Status.length > 0) ||
				(currentFilters.qi100Status && currentFilters.qi100Status.length > 0) ||
				(currentFilters.qi500Status && currentFilters.qi500Status.length > 0) ||
				(currentFilters.qi900Status && currentFilters.qi900Status.length > 0) ||
				(currentFilters.lr99Status && currentFilters.lr99Status.length > 0) ||
				(currentFilters.mr99Status && currentFilters.mr99Status.length > 0) ||
				(currentFilters.ppapStatus && currentFilters.ppapStatus.length > 0) ||
				(currentFilters.bttStatus && currentFilters.bttStatus.length > 0) ||
				(currentFilters.wfIndex && currentFilters.wfIndex.length > 0) ||
				(currentFilters.partStatus && currentFilters.partStatus.length > 0) ||
				(currentFilters.partNoPrefix &&
					currentFilters.partNoPrefix.length > 0) ||
				(currentFilters.wfStufe && currentFilters.wfStufe.length > 0) ||
				(currentFilters.wfBza && currentFilters.wfBza.length > 0) ||
				(currentFilters.wfAlg && currentFilters.wfAlg.length > 0) ||
				(currentFilters.search && currentFilters.search.trim()) ||
				(currentFilters.kastKeys && currentFilters.kastKeys.length > 0);

			console.log(
				"🔍 SerieTrackingDataTable: hasFilters:",
				hasFilters,
				"filters:",
				currentFilters,
			);
			const urlParams = new URLSearchParams();
			urlParams.append("page", page);
			urlParams.append("items_per_page", items);

			// Email-reminder pinned records — always sent first so the backend can AND them
			if (currentFilters.kastKeys && currentFilters.kastKeys.length > 0) {
				currentFilters.kastKeys.forEach((kk) => {
					urlParams.append("kast_keys", kk);
				});
			}

			if (currentFilters.projectNo && currentFilters.projectNo.length > 0) {
				currentFilters.projectNo.forEach((projectNo) => {
					urlParams.append("project_number", projectNo);
				});
			}
			if (currentFilters.alg && currentFilters.alg.length > 0) {
				currentFilters.alg.forEach((alg) => {
					urlParams.append("alg", alg);
				});
			}

			if (currentFilters.programm && currentFilters.programm.length > 0) {
				currentFilters.programm.forEach((programm) => {
					urlParams.append("programm", programm);
				});
			}
			if (currentFilters.changeNo && currentFilters.changeNo.length > 0) {
				currentFilters.changeNo.forEach((changeNo) => {
					urlParams.append("change_no", changeNo);
				});
			}

			if (currentFilters.snr && currentFilters.snr.length > 0) {
				currentFilters.snr.forEach((snr) => {
					urlParams.append("snr", snr);
				});
			}
			if (currentFilters.responsible && currentFilters.responsible.length > 0) {
				currentFilters.responsible.forEach((responsible) => {
					urlParams.append("responsible", responsible);
				});
			}

			if (currentFilters.supplier && currentFilters.supplier.length > 0) {
				currentFilters.supplier.forEach((supplier) => {
					urlParams.append("supplier", supplier);
				});
			}

			if (currentFilters.seTeam && currentFilters.seTeam.length > 0) {
				currentFilters.seTeam.forEach((seTeam) => {
					urlParams.append("se_team", seTeam);
				});
			}
			if (currentFilters.followupDateFrom) {
				urlParams.append("followup_date_from", currentFilters.followupDateFrom);
			}
			if (currentFilters.followupDateTo) {
				urlParams.append("followup_date_to", currentFilters.followupDateTo);
			}

			if (currentFilters.fetFrom) {
				urlParams.append("fet_from", currentFilters.fetFrom);
			}
			if (currentFilters.fetTo) {
				urlParams.append("fet_to", currentFilters.fetTo);
			}
			if (currentFilters.petFrom) {
				urlParams.append("pet_from", currentFilters.petFrom);
			}
			if (currentFilters.petTo) {
				urlParams.append("pet_to", currentFilters.petTo);
			}
			if (currentFilters.sofreiErf && currentFilters.sofreiErf.length > 0) {
				currentFilters.sofreiErf.forEach((v) =>
					urlParams.append("sofrei_erf", v),
				);
			}
			if (currentFilters.sofreiEndeFrom) {
				urlParams.append("sofrei_ende_from", currentFilters.sofreiEndeFrom);
			}
			if (currentFilters.sofreiEndeTo) {
				urlParams.append("sofrei_ende_to", currentFilters.sofreiEndeTo);
			}
			if (currentFilters.bza && currentFilters.bza.length > 0) {
				currentFilters.bza.forEach((bza) => {
					urlParams.append("bza", bza);
				});
			}

			// Add Emcos Milestone filters
			if (currentFilters.a80Status && currentFilters.a80Status.length > 0) {
				currentFilters.a80Status.forEach((a80Status) => {
					urlParams.append("a80_status", a80Status);
				});
			}

			if (currentFilters.e99Status && currentFilters.e99Status.length > 0) {
				currentFilters.e99Status.forEach((e99Status) => {
					urlParams.append("e99_status", e99Status);
				});
			}

			if (currentFilters.p99Status && currentFilters.p99Status.length > 0) {
				currentFilters.p99Status.forEach((p99Status) => {
					urlParams.append("p99_status", p99Status);
				});
			}

			if (currentFilters.qi000Status && currentFilters.qi000Status.length > 0) {
				currentFilters.qi000Status.forEach((qi000Status) => {
					urlParams.append("qi000_status", qi000Status);
				});
			}

			if (currentFilters.qi100Status && currentFilters.qi100Status.length > 0) {
				currentFilters.qi100Status.forEach((qi100Status) => {
					urlParams.append("qi100_status", qi100Status);
				});
			}

			if (currentFilters.qi500Status && currentFilters.qi500Status.length > 0) {
				currentFilters.qi500Status.forEach((qi500Status) => {
					urlParams.append("qi500_status", qi500Status);
				});
			}
			if (currentFilters.qi900Status && currentFilters.qi900Status.length > 0) {
				currentFilters.qi900Status.forEach((qi900Status) => {
					urlParams.append("qi900_status", qi900Status);
				});
			}

			if (currentFilters.lr99Status && currentFilters.lr99Status.length > 0) {
				currentFilters.lr99Status.forEach((lr99Status) => {
					urlParams.append("lr99_status", lr99Status);
				});
			}

			if (currentFilters.mr99Status && currentFilters.mr99Status.length > 0) {
				currentFilters.mr99Status.forEach((mr99Status) => {
					urlParams.append("mr99_status", mr99Status);
				});
			}

			if (currentFilters.ppapStatus && currentFilters.ppapStatus.length > 0) {
				currentFilters.ppapStatus.forEach((ppapStatus) => {
					urlParams.append("ppap_status", ppapStatus);
				});
			}

			if (currentFilters.bttStatus && currentFilters.bttStatus.length > 0) {
				currentFilters.bttStatus.forEach((bttStatus) => {
					urlParams.append("btt_status", bttStatus);
				});
			}

			if (currentFilters.wfIndex && currentFilters.wfIndex.length > 0) {
				currentFilters.wfIndex.forEach((wfIndex) => {
					urlParams.append("wf_index", wfIndex);
				});
			}

			if (currentFilters.partStatus && currentFilters.partStatus.length > 0) {
				currentFilters.partStatus.forEach((ps) => {
					urlParams.append("part_status", ps);
				});
			}

			if (
				currentFilters.partNoPrefix &&
				currentFilters.partNoPrefix.length > 0
			) {
				currentFilters.partNoPrefix.forEach((prefix) => {
					urlParams.append("snr_prefix", prefix);
				});
			}

			// Dashboard click-through filters: wf_stufe and wf_bza
			if (currentFilters.wfStufe && currentFilters.wfStufe.length > 0) {
				currentFilters.wfStufe.forEach((stufe) => {
					urlParams.append("wf_stufe", stufe);
				});
			}
			if (currentFilters.wfBza && currentFilters.wfBza.length > 0) {
				currentFilters.wfBza.forEach((bza) => {
					urlParams.append("wf_bza", bza);
				});
			}

			if (currentFilters.wfAlg && currentFilters.wfAlg.length > 0) {
				currentFilters.wfAlg.forEach((wa) => {
					urlParams.append("wf_alg", wa);
				});
			}
			if (currentFilters.excludeNotr) {
				urlParams.append("exclude_notr", "true");
			}

			if (currentFilters.excludeBttDeleted) {
				urlParams.append("exclude_btt_deleted", "true");
			}

			if (currentFilters.search !== undefined) {
				if (currentFilters.search && currentFilters.search.trim()) {
					urlParams.append("search", currentFilters.search.trim());
				}
			}

			// Add tags filter
			if (currentFilters.tags && currentFilters.tags.length > 0) {
				currentFilters.tags.forEach((tag) => {
					urlParams.append("tags", tag);
				});
			}

			// Add sort parameters if sorting is active
			if (sortOrder !== null) {
				urlParams.append("sort_by", "snr");
				urlParams.append("sort_order", sortOrder);
			}

			console.log(
				"🔍 SerieTrackingDataTable: Making API call with URL:",
				"/api/series-data?" + urlParams.toString(),
			);
			response = await apiClient.get(
				"/api/series-data?" + urlParams.toString(),
			);
			const pagination = response.data.pagination || {
				total_items: 0,
				total_pages: 1,
			};
			const data = response.data.data || []; // Extract comment data inline from each record (same pattern as VFF)
			const newCommentsData = {};
			const newSnrDetailsData = {};
			data.forEach((item) => {
				const key = `${item.snr}_${item.snr_anlage_projnr}`;
				if (key) {
					newCommentsData[key] = {
						latest_comment: item.latest_comment || "",
						latest_comment_user: item.latest_comment_user || "",
						latest_comment_date: item.latest_comment_date || "",
					};
				}
				// Collect SNR detail rows — keyed by SNR so all rows for the same SNR accumulate
				if (item.snr && item.snr_detail && item.snr_detail.length > 0) {
					if (!newSnrDetailsData[item.snr]) {
						newSnrDetailsData[item.snr] = item.snr_detail;
					}
				}
			});

			if (isLoadMore) {
				console.log(
					`📄 Appending ${data.length} new records to existing ${seriesData.length} records`,
				);
				setSeriesData((prevData) => {
					const newData = [...prevData, ...data];
					console.log(
						`📊 Total records after append: ${newData.length}/${pagination.total_items}`,
					);
					return newData;
				});
				setHasMoreData(page < pagination.total_pages);
				console.log(
					`🔍 Infinite scroll status: page ${page}/${pagination.total_pages}, hasMore: ${page < pagination.total_pages}`,
				); // Merge new comments data with existing
				setCommentsData((prev) => ({ ...prev, ...newCommentsData }));
				// Merge new SNR details data with existing
				setSnrDetailsData((prev) => ({ ...prev, ...newSnrDetailsData }));
			} else {
				console.log(
					`🔄 Loading fresh data: ${data.length} records, total pages: ${pagination.total_pages}`,
				);
				setSeriesData(data);
				setHasMoreData(pagination.total_pages > 1);
				// Replace comments data for fresh load
				setCommentsData(newCommentsData);
				// Replace SNR details data for fresh load
				setSnrDetailsData(newSnrDetailsData);
			}

			setTotalRecords(pagination.total_items || data.length);
			setTotalPages(Math.max(1, pagination.total_pages || 1));
			setCurrentPage(page);
		} catch (err) {
			console.error("❌ Error fetching series data:", err);
			console.error("📋 Request details:", {
				page,
				items,
				isLoadMore,
				activeFilters,
			});
			setError("Failed to fetch series data");
			if (!isLoadMore) {
				setSeriesData([]);
				setTotalPages(1);
				setTotalRecords(0);
				setHasMoreData(false);
			} else {
				// Don't set hasMoreData to false on load more error, just stop loading more
				setIsLoadingMore(false);
			}
		} finally {
			if (!isLoadMore) {
				setLoading(false);
			} else {
				setIsLoadingMore(false);
			}
		}
	};
	useEffect(() => {
		// Suppressed while waiting for the parent to supply dashboard filters.
		// suppressAutoFetchRef is set to true at init when skipInitialFetch=true, and
		// cleared inside fetchData() once the parent explicitly calls it.
		if (suppressAutoFetchRef.current) {
			console.log(
				"⏭️ SerieTrackingDataTable: auto-fetch suppressed (waiting for dashboard filters)",
			);
			return;
		}

		if (isManualFetch.current) {
			isManualFetch.current = false;
			return;
		}
		fetchSeriesData(activeFilters, 1, itemsPerPage, false, snrSortOrder);
	}, [activeFilters, itemsPerPage, snrSortOrder]);

	// Auto-dismiss the flag prevention modal once all pending flag updates complete  // Auto-dismiss the concurrent update prevention modal once the blocking operation completes
	useEffect(() => {
		if (!flagUpdateModal.open) return;
		const { blockedBy } = flagUpdateModal;
		const blockingDone =
			(blockedBy === "flagging" &&
				Object.keys(updatingFlagging).length === 0) ||
			(blockedBy === "btt" && Object.keys(updatingBttStatus).length === 0) ||
			(blockedBy === "status" &&
				Object.keys(updatingStatusToggle).length === 0) ||
			(!blockedBy && Object.keys(updatingFlagging).length === 0); // legacy fallback
		if (blockingDone) {
			const timer = setTimeout(() => {
				setFlagUpdateModal((prev) => ({ ...prev, open: false }));
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, [
		updatingFlagging,
		updatingBttStatus,
		updatingStatusToggle,
		flagUpdateModal.open,
		flagUpdateModal.blockedBy,
	]);
	const currentData = seriesData.map((item, index) => {
		const deviations = [
			{ type: "Deviation", value: item.emu_status_ppap || "" },
			{ type: "Special release needed", value: item.sofrei_erf || "" },
			{ type: "Special release until", value: item.sofrei_ende || "" },
			{
				type: "Special release transaction no",
				value: item.sofrei_vorgangnr || "",
			},
			// { type: 'Quality responsible', value: item.q_bearb || '' }
		];
		const dates = [
			{ label: "MET", value: item.met || "" },
			// { label: 'Logistics responsible', value: item.vsd_bearb || '' }
		];

		const milestones = item.milestones || {};
		return {
			id: item.id || `${item.alg}_${item.snr}_${index}`,
			alg: item.alg || "",
			SNR: item.snr || "",
			description: item.bend || "",
			project: item.snr_anlage_projnr || "",
			changeNo: item.aendnr || "",
			wf_index: item.wf_index || "",
			met: item.met || "",
			supplier: item.lieferant || "",
			purchaseType: item.bezugsart_plan || "",
			procResponsible: item.b_bearb || "",
			nomination: "",
			seTeam: item.se_team || "",
			deviations: deviations,
			dates: dates,
			milestones: milestones,
			milestones_detail: item.milestones_detail || {},
			milestone_status: item.milestone_status || {},
			stockStatus: item.stock_status || {},
			status_toggle: item.status_toggle || null,
			btt_status: item.btt_status || "",
			flagging: item.flagging || "N",
			serie_status: item.serie_status || "",
			snr_status: item.snr_status || "",
			tags: item.tags || "",
			part_entry_date: item.part_entry_date || "",
			status: {
				responsible: item.atr_verantwortlich || "",
				followUp: item.atr_wiedervorlage || "",
				comment: 0,
			},
		};
	});

	const handleViewMilestones = (rowData) => {
		setSelectedRowData(rowData);
		setIsModalOpen(true);
	};
	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedRowData(null);
	};

	const handleMilestoneUpdate = (milestoneKey, newVdatValue) => {
		// Update the series data with new VDAT value
		setSeriesData((prevData) =>
			prevData.map((item) => {
				if (
					item.snr === selectedRowData.snr &&
					item.snr_anlage_projnr === selectedRowData.snr_anlage_projnr
				) {
					return {
						...item,
						milestones_detail: {
							...item.milestones_detail,
							[milestoneKey]: {
								...item.milestones_detail[milestoneKey],
								vdat: newVdatValue,
							},
						},
					};
				}
				return item;
			}),
		);

		// Also update selectedRowData for immediate modal update
		setSelectedRowData((prevData) => ({
			...prevData,
			milestones_detail: {
				...prevData.milestones_detail,
				[milestoneKey]: {
					...prevData.milestones_detail[milestoneKey],
					vdat: newVdatValue,
				},
			},
		}));
	};
	const getMilestoneColor = (status) => {
		if (!status || status === "-") return "#ffffff"; // White background for unassigned
		const statusUpper = status.toString().toUpperCase();
		if (statusUpper === "OK") return "#28a745"; // Green for OK
		if (statusUpper === "NRV") return "#dc3545"; // Red for NRV
		if (statusUpper === "NOK") return "#666"; // Grey for NOK
		return "#ffffff"; // Default to white background
	};

	const getMilestoneBorderColor = (status) => {
		if (!status || status === "-") return "#000"; // black border for unassigned
		const statusUpper = status.toString().toUpperCase();
		if (statusUpper === "OK") return "#28a745"; // Green border for OK
		if (statusUpper === "NRV") return "#dc3545"; // Red border for NRV
		if (statusUpper === "NOK") return "#666"; // Grey border for NOK
		return "#000"; // Default to black border
	};
	const getMilestoneBackgroundColor = (currentStatus, nextStatus) => {
		const currentStatusUpper = currentStatus
			? currentStatus.toString().toUpperCase()
			: "";
		const nextStatusUpper = nextStatus
			? nextStatus.toString().toUpperCase()
			: "";

		if (currentStatusUpper === "OK" && nextStatusUpper === "OK") {
			return "#28a745";
		}
		return "transparent";
	};

	const handleOpenCommentModal = (row) => {
		console.log("📝 Opening comment modal for series:", {
			snr: row.SNR,
			project: row.project,
		});
		setSelectedCommentSeries({
			snr: row.SNR,
			snrAnlageProjnr: row.project,
			seriesId: `${row.SNR}_${row.project}`,
		});
		setNewComment("");
		setEditingCommentId(null);
		setEditText("");
		setCommentModalOpen(true);
	};

	const handleCloseCommentModal = () => {
		setCommentModalOpen(false);
		setSelectedCommentSeries(null);
		setNewComment("");
		setEditingCommentId(null);
		setEditText("");
	};
	const handleCommentAdded = (
		latestCommentText,
		latestCommentUser,
		latestCommentDate,
	) => {
		console.log("✅ Comment added successfully");
		// Update commentsData inline so the icon turns dark immediately (same pattern as VFF)
		if (selectedCommentSeries) {
			const seriesKey = `${selectedCommentSeries.snr}_${selectedCommentSeries.snrAnlageProjnr}`;
			setCommentsData((prev) => ({
				...prev,
				[seriesKey]: {
					latest_comment:
						latestCommentText || prev[seriesKey]?.latest_comment || "",
					latest_comment_user:
						latestCommentUser || prev[seriesKey]?.latest_comment_user || "",
					latest_comment_date:
						latestCommentDate || prev[seriesKey]?.latest_comment_date || "",
				},
			}));
		}
	};

	// Side panel handlers
	const handleOpenSidePanel = (row) => {
		setSelectedSidePanelRow(row);
		setSidePanelOpen(true);
	};
	const handleCloseSidePanel = () => {
		setSidePanelOpen(false);
		setSelectedSidePanelRow(null);
	};

	// SNR Detail modal handlers
	const handleOpenSnrDetail = (snr) => {
		setSnrDetailSnr(snr);
		setSnrDetailModalOpen(true);
		// Look up pre-fetched data from the snrDetailsData map (populated on page load)
		const rows = snrDetailsData[snr];
		if (rows && rows.length > 0) {
			setSnrDetailData({ found: true, data: rows });
		} else {
			setSnrDetailData({ found: false, data: [] });
		}
	};

	const handleCloseSnrDetail = () => {
		setSnrDetailModalOpen(false);
		setSnrDetailData(null);
		setSnrDetailSnr(null);
	};
	const handleEnableEditMode = () => {
		setIsGlobalEditMode(true);
		const newEditingResponsible = {};
		const newEditingFollowup = {};
		const newResponsibleValues = {};
		const newFollowupValues = {};
		const newFlaggingValues = {};

		// Use the processed currentData instead of raw seriesData
		const processedData = seriesData.map((item, index) => ({
			id: item.id || `${item.alg}_${item.snr}_${index}`,
			SNR: item.snr || "",
			project: item.snr_anlage_projnr || "",
			flagging: item.flagging || "N",
			status: {
				responsible: item.atr_verantwortlich || "",
				followUp: item.atr_wiedervorlage || "",
				comment: 0,
			},
		}));

		processedData.forEach((row) => {
			const itemId = `${row.SNR}_${row.project}`;
			newEditingResponsible[itemId] = true;
			newEditingFollowup[itemId] = true;

			// Capture current flagging value
			newFlaggingValues[itemId] = row.flagging || "N";

			// Safely access status with fallback
			const responsible = row.status?.responsible || "";
			const cleanResponsible =
				responsible === "<NA>" || !responsible ? "" : responsible;
			newResponsibleValues[itemId] = cleanResponsible;
			const followUp = row.status?.followUp || "";
			if (followUp && followUp !== "-") {
				try {
					const [day, month, year] = followUp.split("/");
					// Validate the date parts before creating Date object
					if (
						day &&
						month &&
						year &&
						!isNaN(day) &&
						!isNaN(month) &&
						!isNaN(year)
					) {
						const parsedDate = new Date(
							parseInt(year),
							parseInt(month) - 1,
							parseInt(day),
						);
						// Double-check that the created date is valid
						if (!isNaN(parsedDate.getTime())) {
							newFollowupValues[itemId] = parsedDate;
						} else {
							console.warn("Invalid date created from:", followUp);
							newFollowupValues[itemId] = null;
						}
					} else {
						console.warn("Invalid date parts from:", followUp);
						newFollowupValues[itemId] = null;
					}
				} catch (e) {
					console.warn("Error parsing date:", followUp, e);
					newFollowupValues[itemId] = null;
				}
			} else {
				newFollowupValues[itemId] = null;
			}
		});

		setEditingResponsible(newEditingResponsible);
		setEditingFollowup(newEditingFollowup);
		setResponsibleValues(newResponsibleValues);
		setFollowupValues(newFollowupValues);
		setFlaggingValues(newFlaggingValues);
	};

	const handleCancelEditMode = () => {
		setIsGlobalEditMode(false);
		setEditingResponsible({});
		setEditingFollowup({});
		setResponsibleValues({});
		setFollowupValues({});
		setFlaggingValues({});
	};
	const handleBulkSave = async () => {
		// Check write access first
		if (!canWrite) {
			setShowAccessDeniedModal(true);
			console.warn(
				"🚫 User attempted to bulk save without write access:",
				userEmail,
			);
			return;
		}

		try {
			setIsBulkSaving(true);

			const responsibleUpdates = [];
			const followupUpdates = [];
			const flaggingUpdates = [];

			// Use the same processed data structure as in handleEnableEditMode
			const processedData = seriesData.map((item, index) => ({
				id: item.id || `${item.alg}_${item.snr}_${index}`,
				SNR: item.snr || "",
				project: item.snr_anlage_projnr || "",
				flagging: item.flagging || "N",
				status: {
					responsible: item.atr_verantwortlich || "",
					followUp: item.atr_wiedervorlage || "",
					comment: 0,
				},
			}));

			processedData.forEach((row) => {
				const itemId = `${row.SNR}_${row.project}`;

				// Flagging changes
				const currentFlagging = row.flagging || "N";
				const newFlagging = flaggingValues[itemId];
				if (newFlagging !== undefined && newFlagging !== currentFlagging) {
					flaggingUpdates.push({
						snr: row.SNR,
						snr_anlage_projnr: row.project,
						value: newFlagging,
					});
				}

				// Responsible changes
				const responsible = row.status?.responsible || "";
				const currentResponsible =
					responsible === "<NA>" || !responsible ? "" : responsible;
				const newResponsible = responsibleValues[itemId];
				if (
					newResponsible !== undefined &&
					newResponsible !== currentResponsible
				) {
					responsibleUpdates.push({
						snr: row.SNR,
						snr_anlage_projnr: row.project,
						value: newResponsible,
					});
				}

				const newFollowupDate = followupValues[itemId];
				if (newFollowupDate !== undefined) {
					const formattedNewDate = newFollowupDate
						? formatDateForBackend(newFollowupDate)
						: null;

					let currentFormattedDate = null;
					const followUp = row.status?.followUp || "";
					if (followUp && followUp !== "-") {
						try {
							const [day, month, year] = followUp.split("/");
							currentFormattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
						} catch (e) {
							currentFormattedDate = null;
						}
					}

					if (formattedNewDate !== currentFormattedDate) {
						followupUpdates.push({
							snr: row.SNR,
							snr_anlage_projnr: row.project,
							value: formattedNewDate,
						});
					}
				}
			});
			console.log("📝 Bulk updates to process:", {
				flagging: flaggingUpdates.length,
				responsible: responsibleUpdates.length,
				followup: followupUpdates.length,
			});

			const totalChanges =
				flaggingUpdates.length +
				responsibleUpdates.length +
				followupUpdates.length;

			if (totalChanges > 0) {
				// Build one combined payload for /api/bulk-update-series-comprehensive
				// which applies ALL field changes in a single CASE-based Athena query.
				const comprehensiveUpdates = {};

				flaggingUpdates.forEach((u) => {
					const key = `${u.snr}||${u.snr_anlage_projnr}`;
					if (!comprehensiveUpdates[key])
						comprehensiveUpdates[key] = {
							snr: u.snr,
							snr_anlage_projnr: u.snr_anlage_projnr,
							updates: {},
						};
					comprehensiveUpdates[key].updates.flagging = u.value;
				});
				responsibleUpdates.forEach((u) => {
					const key = `${u.snr}||${u.snr_anlage_projnr}`;
					if (!comprehensiveUpdates[key])
						comprehensiveUpdates[key] = {
							snr: u.snr,
							snr_anlage_projnr: u.snr_anlage_projnr,
							updates: {},
						};
					comprehensiveUpdates[key].updates.responsible = u.value;
				});
				followupUpdates.forEach((u) => {
					const key = `${u.snr}||${u.snr_anlage_projnr}`;
					if (!comprehensiveUpdates[key])
						comprehensiveUpdates[key] = {
							snr: u.snr,
							snr_anlage_projnr: u.snr_anlage_projnr,
							updates: {},
						};
					comprehensiveUpdates[key].updates.followup_date = u.value; // ISO yyyy-MM-dd or null
				});

				const payload = {
					updates: Object.values(comprehensiveUpdates),
					user_name: props.getUserDisplayName
						? props.getUserDisplayName()
						: "Current User",
				};
				console.log(
					"🚀 Sending single bulk-update-series-comprehensive request for",
					payload.updates.length,
					"records",
				);
				const result = await apiClient.post(
					"/api/bulk-update-series-comprehensive",
					payload,
					{
						headers: {
							"X-User-Name": props.getUserEmail
								? props.getUserEmail()
								: "Unknown",
						},
					},
				);
				console.log(
					"✅ bulk-update-series-comprehensive completed:",
					result.data,
				);

				// Update local state to reflect saved values immediately
				setSeriesData((prev) =>
					prev.map((item) => {
						const snr = item.snr || "";
						const project = item.snr_anlage_projnr || "";

						const flaggingUpdate = flaggingUpdates.find(
							(u) => u.snr === snr && u.snr_anlage_projnr === project,
						);
						const responsibleUpdate = responsibleUpdates.find(
							(u) => u.snr === snr && u.snr_anlage_projnr === project,
						);
						const followupUpdate = followupUpdates.find(
							(u) => u.snr === snr && u.snr_anlage_projnr === project,
						);

						if (flaggingUpdate || responsibleUpdate || followupUpdate) {
							const updatedItem = { ...item };
							if (flaggingUpdate) updatedItem.flagging = flaggingUpdate.value;
							if (responsibleUpdate)
								updatedItem.atr_verantwortlich = responsibleUpdate.value || "";
							if (followupUpdate) {
								if (followupUpdate.value) {
									try {
										const date = new Date(followupUpdate.value);
										updatedItem.atr_wiedervorlage = !isNaN(date.getTime())
											? format(date, "dd/MM/yyyy")
											: "";
									} catch (e) {
										updatedItem.atr_wiedervorlage = "";
									}
								} else {
									updatedItem.atr_wiedervorlage = "";
								}
							}
							return updatedItem;
						}
						return item;
					}),
				);

				const updatedRecords = new Set([
					...flaggingUpdates.map((u) => `${u.snr}_${u.snr_anlage_projnr}`),
					...responsibleUpdates.map((u) => `${u.snr}_${u.snr_anlage_projnr}`),
					...followupUpdates.map((u) => `${u.snr}_${u.snr_anlage_projnr}`),
				]);
				console.log(
					`📊 Updated ${totalChanges} field(s) across ${updatedRecords.size} record(s)`,
				);
			}

			setIsGlobalEditMode(false);
			setEditingResponsible({});
			setEditingFollowup({});
			setResponsibleValues({});
			setFollowupValues({});
			setFlaggingValues({});
		} catch (error) {
			console.error("❌ Error performing bulk save:", error);
			// 403 = access denied — show the modal instead of alert
			if (error.response?.status === 403) {
				refreshAccess();
				setShowAccessDeniedModal(true);
				return;
			}

			let userMessage = "Failed to save changes. Please try again.";
			if (error.response?.data?.detail) {
				userMessage = error.response.data.detail;
			} else if (error.message && error.message !== "Network Error") {
				userMessage = error.message;
			}

			alert(userMessage);
		} finally {
			setIsBulkSaving(false);
		}
	};

	const styles = {
		container: {
			width: "100%",
			maxWidth: "100%",
			backgroundColor: "#fff",
			fontFamily: "MAN EUROPE_Regular",
			fontSize: "12px",
			height: "100%",
			display: "flex",
			flexDirection: "column",
			position: "relative",
			overflow: "hidden",
		},
		tableWrapper: {
			flex: 1,
			overflow: "auto",
			border: "1px solid #e5e7eb",
			position: "relative",
			marginBottom: "10px",
			paddingBottom: "45px",
		},
		table: {
			width: "100%",
			borderCollapse: "collapse",
			backgroundColor: "#fff",
			tableLayout: "auto",
			minWidth: "1200px",
		},
		thead: {
			backgroundColor: "#f8f9fa",
			position: "sticky",
			top: 0,
			zIndex: 10,
			borderBottom: "1px solid #e5e7eb",
		},
		th: {
			padding: "2px",
			textAlign: "center",
			fontWeight: "500",
			color: "black",
			borderBottom: "1px solid #e5e7eb",
			borderRight: "none",
			backgroundColor: "#f8f9fa",
			fontFamily: "MAN EUROPE_Bold",
		},
		td: {
			padding: "2px 6px",
			borderBottom: "1px solid #e5e7eb",
			borderRight: "none",
			verticalAlign: "top",
			fontSize: "11px",
			color: "#374151",
			fontFamily: "MAN EUROPE_Regular",
			lineHeight: "1.2",
			minHeight: "30px",
		},
		badge: {
			display: "inline-block",
			padding: "2px 6px",
			borderRadius: "2px",
			fontSize: "9px",
			fontWeight: "500",
			whiteSpace: "nowrap",
			fontFamily: "MAN EUROPE_Regular",
		},
		blueBadge: {
			backgroundColor: "#e7f3ff",
			color: "#0066cc",
			border: "1px solid #b3d9ff",
		},
		pinkBadge: {
			backgroundColor: "#ffe7f3",
			color: "#cc0066",
			border: "1px solid #ffb3d9",
		},
		progressBarContainer: {
			width: "100%",
			height: "8px",
			backgroundColor: "#f3f4f6",
			borderRadius: "2px",
			overflow: "hidden",
			marginTop: "2px",
		},
		progressBar: {
			height: "100%",
			transition: "width 0.3s ease",
		},
		cyanBar: {
			backgroundColor: "#17a2b8",
		},
		grayBar: {
			backgroundColor: "#6c757d",
		},
		actionBtn: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "4px",
			display: "flex",
			alignItems: "center",
			borderRadius: "2px",
		},
		stockRow: {
			display: "grid",
			gridTemplateColumns: "30px 72px 72px 44px 62px",
			alignItems: "center",
			gap: "4px",
			marginBottom: "1px",
			fontSize: "8px",
			fontFamily: "MAN EUROPE_Regular",
			paddingLeft: "4px",
			paddingRight: "4px",
		},
		stockLabel: {
			color: "#374151",
			fontWeight: "600",
			textAlign: "center",
			fontSize: "11px",
			fontFamily: "MAN EUROPE_Bold",
		},
		stockValue: {
			color: "black",
			fontFamily: "MAN EUROPE_Regular",
			fontSize: "10px",
			textAlign: "center",
			padding: "2px 3px",
		},
		stockBar: {
			width: "40px",
			height: "6px",
			backgroundColor: "#f3f4f6",
			borderRadius: "1px",
		},
		viewMoreButton: {
			backgroundColor: "#666",
			color: "white",
			border: "none",
			borderRadius: "50%",
			padding: "2px",
			width: "16px",
			height: "16px",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			fontFamily: "MAN EUROPE_Regular",
			transition: "background-color 0.2s ease",
			position: "absolute",
			zIndex: 3,
			"&:hover": {
				backgroundColor: "#444",
			},
		},
	};
	if (loading) {
		return (
			<div style={styles.container}>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						marginTop: "150px",
					}}
				>
					<Box
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						height="100%"
						minHeight="300px"
					>
						<CircularProgress
							size={60}
							thickness={4}
							sx={{ color: "#cc0000" }}
						/>
						<Typography
							variant="body1"
							style={{
								marginTop: "20px",
								color: "#666",
								fontFamily: "MAN EUROPE_Regular",
							}}
						>
							Loading series data...
						</Typography>
					</Box>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div style={styles.container}>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "400px",
						backgroundColor: "#f8f9fa",
						borderRadius: "8px",
						margin: "20px",
					}}
				>
					<div style={{ textAlign: "center", color: "#dc3545" }}>
						<div
							style={{
								fontSize: "18px",
								marginBottom: "8px",
								fontFamily: "MAN EUROPE_Bold",
							}}
						>
							Error Loading Data
						</div>
						<div style={{ fontSize: "14px", fontFamily: "MAN EUROPE_Regular" }}>
							{error}
						</div>{" "}
						<button
							onClick={() =>
								fetchSeriesData(activeFilters, 1, itemsPerPage, false)
							}
							style={{
								marginTop: "16px",
								padding: "8px 16px",
								backgroundColor: "#0066cc",
								color: "white",
								border: "none",
								borderRadius: "4px",
								cursor: "pointer",
								fontFamily: "MAN EUROPE_Regular",
							}}
						>
							Retry
						</button>
					</div>
				</div>
			</div>
		);
	}

	if (!loading && currentData.length === 0) {
		return (
			<div style={styles.container}>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "400px",
						backgroundColor: "#f8f9fa",
						borderRadius: "8px",
						margin: "20px",
					}}
				>
					<div style={{ textAlign: "center", color: "#666" }}>
						<div
							style={{
								fontSize: "18px",
								marginBottom: "8px",
								fontFamily: "MAN EUROPE_Bold",
							}}
						>
							No Data Available
						</div>
						<div style={{ fontSize: "14px", fontFamily: "MAN EUROPE_Regular" }}>
							No series data found in the database.
						</div>
					</div>
				</div>
			</div>
		);
	}
	return (
		<div style={styles.container}>
			<div style={styles.tableWrapper} onScroll={handleScroll}>
				<table style={styles.table}>
					<thead style={styles.thead}>
						<tr>
							<th
								style={{
									...styles.th,
									width: "55px",
									minWidth: "50px",
									padding: "4px",
									textAlign: "center",
									borderLeft: "3px solid transparent",
									position: "relative",
								}}
							>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "4px",
										flexWrap: "nowrap",
									}}
								>
									<div
										style={{
											position: "relative",
											display: "inline-flex",
											alignItems: "center",
										}}
									>
										<input
											ref={checkboxRef}
											type="checkbox"
											checked={allSelected}
											onChange={() => {}}
											onClick={handleSelectAllClick}
											style={{ cursor: "pointer" }}
											title="Click to open selection options (select all or pick a range)"
										/>
										{rangePopoverOpen && (
											<div
												ref={rangePopoverRef}
												style={{
													position: "absolute",
													top: "24px",
													left: "0",
													zIndex: 999,
													backgroundColor: "#fff",
													border: "1px solid #d1d5db",
													borderRadius: "8px",
													boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
													padding: "12px",
													width: "230px",
													fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
												}}
											>
												{/* Header */}
												<div
													style={{
														display: "flex",
														justifyContent: "space-between",
														alignItems: "center",
														marginBottom: "10px",
													}}
												>
													<span
														style={{
															fontSize: "12px",
															fontWeight: "600",
															color: "#111827",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														Select Rows
													</span>
													<button
														onClick={() => setRangePopoverOpen(false)}
														style={{
															background: "none",
															border: "none",
															cursor: "pointer",
															padding: "0",
															color: "#6b7280",
															fontSize: "14px",
															lineHeight: 1,
														}}
														title="Close"
													>
														✕
													</button>
												</div>
												{/* Select All button */}
												<button
													onClick={() => {
														const pageData = seriesData.map((item) => ({
															...item,
															id: `${item.snr}_${item.snr_anlage_projnr}`,
															kast_key: `${item.snr}_${item.snr_anlage_projnr}`,
															vehicle_no: item.snr,
															part_no: item.snr,
															part_description: item.bend,
															supplier: item.lieferant,
															required_date: item.met,
														}));
														onSelectItems?.(pageData);
														setAllSelected(true);
														setRangePopoverOpen(false);
													}}
													style={{
														width: "100%",
														fontSize: "12px",
														padding: "6px 8px",
														backgroundColor: "#1d4ed8",
														color: "white",
														border: "none",
														borderRadius: "5px",
														cursor: "pointer",
														fontFamily: "MAN EUROPE_Bold",
														marginBottom: "10px",
													}}
												>
													{" "}
													Select All Loaded ({seriesData.length})
												</button>
												{/* Deselect All button */}
												<button
													onClick={() => {
														onSelectItems?.([]);
														setAllSelected(false);
														setRangePopoverOpen(false);
													}}
													disabled={selectedItems.length === 0}
													style={{
														width: "100%",
														fontSize: "12px",
														padding: "6px 8px",
														backgroundColor:
															selectedItems.length === 0
																? "#e5e7eb"
																: "#dc3545",
														color:
															selectedItems.length === 0 ? "#9ca3af" : "white",
														border: "none",
														borderRadius: "5px",
														cursor:
															selectedItems.length === 0
																? "not-allowed"
																: "pointer",
														fontFamily: "MAN EUROPE_Bold",
														marginBottom: "10px",
													}}
												>
													Deselect All ({selectedItems.length})
												</button>
												{/* Divider */}
												<div
													style={{
														borderTop: "1px solid #e5e7eb",
														marginBottom: "10px",
													}}
												/>
												{/* Range label */}
												<div
													style={{
														fontSize: "11px",
														color: "#6b7280",
														marginBottom: "8px",
													}}
												>
													Or select by row number range:
												</div>
												{/* From / To inputs */}
												<div
													style={{
														display: "flex",
														gap: "8px",
														alignItems: "center",
														marginBottom: "8px",
													}}
												>
													<div style={{ flex: 1 }}>
														<label
															style={{
																fontSize: "10px",
																color: "#6b7280",
																display: "block",
																marginBottom: "2px",
															}}
														>
															From
														</label>
														<input
															type="number"
															min="1"
															max={seriesData.length}
															value={rangeFrom}
															onChange={(e) => {
																setRangeFrom(e.target.value);
																setRangeError("");
															}}
															onKeyDown={(e) =>
																e.key === "Enter" && handleApplyRangeSelection()
															}
															placeholder="e.g. 250"
															style={{
																width: "100%",
																padding: "5px 6px",
																border: "1px solid #d1d5db",
																borderRadius: "4px",
																fontSize: "12px",
																boxSizing: "border-box",
																outline: "none",
															}}
														/>
													</div>
													<div style={{ flex: 1 }}>
														<label
															style={{
																fontSize: "10px",
																color: "#6b7280",
																display: "block",
																marginBottom: "2px",
															}}
														>
															To
														</label>
														<input
															type="number"
															min="1"
															max={seriesData.length}
															value={rangeTo}
															onChange={(e) => {
																setRangeTo(e.target.value);
																setRangeError("");
															}}
															onKeyDown={(e) =>
																e.key === "Enter" && handleApplyRangeSelection()
															}
															placeholder="e.g. 350"
															style={{
																width: "100%",
																padding: "5px 6px",
																border: "1px solid #d1d5db",
																borderRadius: "4px",
																fontSize: "12px",
																boxSizing: "border-box",
																outline: "none",
															}}
														/>
													</div>
												</div>
												{rangeError && (
													<div
														style={{
															fontSize: "11px",
															color: "#dc2626",
															marginBottom: "8px",
															lineHeight: 1.4,
														}}
													>
														⚠ {rangeError}
													</div>
												)}
												<div
													style={{
														fontSize: "10px",
														color: "#9ca3af",
														marginBottom: "8px",
														lineHeight: 1.4,
													}}
												>
													Row numbers match the Sr No. column. Max loaded:{" "}
													{seriesData.length}.
												</div>
												<button
													onClick={handleApplyRangeSelection}
													style={{
														width: "100%",
														fontSize: "12px",
														padding: "6px 8px",
														backgroundColor: "#cc0000",
														color: "white",
														border: "none",
														borderRadius: "5px",
														cursor: "pointer",
														fontFamily: "MAN EUROPE_Bold",
													}}
												>
													Apply Range
												</button>
											</div>
										)}
									</div>
									{selectedItems.length > 0 && (
										<button
											onClick={handleDeselectAll}
											style={{
												fontSize: "11px",
												padding: "2px 5px",
												backgroundColor: "#dc3545",
												color: "white",
												border: "none",
												borderRadius: "3px",
												cursor: "pointer",
												fontFamily: "MAN EUROPE_Bold",
												transition: "background-color 0.2s ease",
												whiteSpace: "nowrap",
											}}
											onMouseOver={(e) =>
												(e.target.style.backgroundColor = "#c82333")
											}
											onMouseOut={(e) =>
												(e.target.style.backgroundColor = "#dc3545")
											}
											title={`Deselect all ${selectedItems.length} selected item${selectedItems.length !== 1 ? "s" : ""}`}
										>
											Clear ({selectedItems.length})
										</button>
									)}
								</div>
							</th>
							<th style={{ ...styles.th, width: "35px", minWidth: "30px" }}>
								Sr No.
							</th>
							<th style={{ ...styles.th, width: "270px", minWidth: "200px" }}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
									}}
								>
									<span>Description</span>
									<button
										onClick={handleSnrSort}
										style={{
											background: "transparent",
											border: "none",
											cursor: "pointer",
											padding: "4px",
											display: "flex",
											alignItems: "center",
											color: snrSortOrder !== null ? "#cc0000" : "#999",
											transition: "color 0.2s ease",
											opacity: snrSortOrder !== null ? 1 : 0.5,
										}}
										title={
											snrSortOrder === null
												? "Click to sort by Part Number (Ascending)"
												: snrSortOrder === "asc"
													? "Sorted by Part Number (Ascending) - Click to sort Descending"
													: "Sorted by Part Number (Descending) - Click to sort Ascending"
										}
									>
										{snrSortOrder === "asc" ? (
											<ChevronUp size={16} />
										) : snrSortOrder === "desc" ? (
											<ChevronDown size={16} />
										) : (
											<ChevronUp size={16} />
										)}
									</button>
								</div>
							</th>
							<th
								style={{ ...styles.th, width: "260px", minWidth: "220px" }}
							></th>
							<th style={{ ...styles.th, width: "260px", minWidth: "220px" }}>
								Milestone
							</th>
							<th
								style={{ ...styles.th, width: "240px", minWidth: "180px" }}
							></th>
							<th style={{ ...styles.th, width: "340px", minWidth: "300px" }}>
								<div style={{ marginBottom: "4px", fontWeight: "600" }}>
									<Edit2
										size={12}
										style={{
											color: "#cc0000",
											opacity: 0.7,
											position: "relative",
											right: "3px",
										}}
									/>
									Stock Status
								</div>
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "30px 72px 72px 44px 62px",
										gap: "4px",
										fontSize: "8px",
										fontWeight: "400",
										color: "#6b7280",
										fontFamily: "MAN EUROPE_Regular",
										paddingLeft: "4px",
										paddingRight: "4px",
									}}
								>
									<span style={{ textAlign: "center" }}>Plant</span>
									<span style={{ textAlign: "center" }}>Req.dt</span>
									<span style={{ textAlign: "center" }}>Next Delivery.dt</span>
									<span style={{ textAlign: "center" }}>Stock</span>
									<span style={{ textAlign: "center" }}></span>
								</div>
							</th>
							<th style={{ ...styles.th, width: "180px", minWidth: "160px" }}>
								<Edit2
									size={12}
									style={{
										color: "#cc0000",
										opacity: 0.7,
										position: "relative",
										right: "3px",
									}}
								/>
								BTT Status
							</th>
							<th style={{ ...styles.th, width: "85px", minWidth: "80px" }}>
								<Edit2
									size={12}
									style={{
										color: "#cc0000",
										opacity: 0.7,
										position: "relative",
										right: "3px",
									}}
								/>
								Status
								{isGlobalEditMode && (
									<div
										style={{
											fontSize: "8px",
											color: "#cc0000",
											fontFamily: "MAN EUROPE_Regular",
											fontWeight: 400,
											marginTop: "2px",
										}}
									></div>
								)}
							</th>
						</tr>
					</thead>
					<tbody>
						{currentData.map((row, idx) => {
							const itemId = `${row.SNR}_${row.project}`;
							const isSelected = selectedItems.some(
								(selected) =>
									selected.id === itemId || selected.kast_key === itemId,
							);

							return (
								<React.Fragment key={row.id}>
									<tr
										style={{
											height: "30px",
											backgroundColor: idx % 2 === 0 ? "#fff" : "#fafbfc",
										}}
									>
										{/* Checkbox + Flag column */}
										<td
											style={{
												...styles.td,
												textAlign: "center",
												verticalAlign: "top",
												borderBottom: "none",
												paddingTop: "8px",
												paddingBottom: "1px",
												width: "35px",
												minWidth: "30px",
												borderLeft:
													(isGlobalEditMode
														? flaggingValues[itemId]
														: row.flagging) === "Y"
														? "3px solid #ff6b35"
														: "3px solid transparent",
											}}
										>
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													gap: "4px",
												}}
											>
												<input
													type="checkbox"
													checked={isSelected}
													onChange={() => handleSelectItem(row)}
													style={{ cursor: "pointer" }}
													title={`Select ${row.SNR}`}
												/>
												{/* In edit mode: toggle flaggingValues locally; outside edit mode: fire API immediately */}
												{isGlobalEditMode ? (
													<button
														onClick={() => {
															const current = flaggingValues[itemId] || "N";
															setFlaggingValues((prev) => ({
																...prev,
																[itemId]: current === "Y" ? "N" : "Y",
															}));
														}}
														style={{
															background: "none",
															border: "none",
															cursor: "pointer",
															padding: "2px",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															transition: "transform 0.1s ease",
														}}
														title={
															(flaggingValues[itemId] || "N") === "Y"
																? "Flagged – click to unflag"
																: "Not flagged – click to flag"
														}
														onMouseEnter={(e) => {
															e.currentTarget.style.transform = "scale(1.2)";
														}}
														onMouseLeave={(e) => {
															e.currentTarget.style.transform = "scale(1)";
														}}
													>
														<Flag
															size={14}
															color={
																(flaggingValues[itemId] || "N") === "Y"
																	? "#ff6b35"
																	: "#aaa"
															}
															fill={
																(flaggingValues[itemId] || "N") === "Y"
																	? "#ff6b35"
																	: "none"
															}
														/>
													</button>
												) : updatingFlagging[itemId] ? (
													<CircularProgress
														size={12}
														thickness={5}
														sx={{ color: "#cc0000" }}
													/>
												) : (
													<button
														onClick={() =>
															handleFlaggingToggle(
																row.SNR,
																row.project,
																row.flagging,
															)
														}
														disabled={!canWrite}
														style={{
															background: "none",
															border: "none",
															cursor: canWrite ? "pointer" : "not-allowed",
															padding: "2px",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															opacity: canWrite ? 1 : 0.5,
															transition: "transform 0.1s ease",
														}}
														title={
															row.flagging === "Y"
																? "Flagged – click to unflag"
																: "Not flagged – click to flag"
														}
														onMouseEnter={(e) => {
															if (canWrite)
																e.currentTarget.style.transform = "scale(1.2)";
														}}
														onMouseLeave={(e) => {
															e.currentTarget.style.transform = "scale(1)";
														}}
													>
														<Flag
															size={14}
															color={row.flagging === "Y" ? "#ff6b35" : "#aaa"}
															fill={row.flagging === "Y" ? "#ff6b35" : "none"}
														/>{" "}
													</button>
												)}
												{/* Status double-box: box1 = serie_tracking.status, box2 = t_630_umfang_serie.snr_status */}
												<StatusDoubleBox
													serieStatus={row.serie_status}
													snrStatus={row.snr_status}
												/>
											</div>
										</td>
										{/* Serial number column */}
										<td
											style={{
												...styles.td,
												textAlign: "center",
												verticalAlign: "top",
												borderBottom: "none",
												paddingTop: "1px",
												paddingBottom: "1px",
											}}
										>
											<span
												style={{
													fontWeight: "600",
													fontFamily: "MAN EUROPE_Bold",
												}}
											>
												{idx + 1}.
											</span>
										</td>
										<td
											style={{
												...styles.td,
												textAlign: "left",
												verticalAlign: "top",
												borderBottom: "none",
												paddingTop: "2px",
												paddingBottom: "1px",
											}}
										>
											<div style={{ lineHeight: "1.1", fontSize: "11px" }}>
												<div style={{ marginBottom: "1px" }}>
													<span
														style={{
															fontWeight: "600",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														ALG:
													</span>{" "}
													<span
														style={{
															color: "#0066cc",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														{row.alg}
													</span>
												</div>
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: "6px",
														marginBottom: "1px",
													}}
												>
													<span
														onClick={() => handleOpenSnrDetail(row.SNR)}
														style={{
															fontFamily: "MAN EUROPE_Bold",
															fontSize: "16px",
															color: "#0066cc",
															cursor: "pointer",
														}}
														title={`Click to view details for ${row.SNR}`}
														onMouseEnter={(e) => {
															e.currentTarget.style.color = "#cc0000";
														}}
														onMouseLeave={(e) => {
															e.currentTarget.style.color = "#0066cc";
														}}
													>
														{row.SNR}
													</span>
													<button
														data-tour-target="serie-more-info"
														onClick={() => handleOpenSidePanel(row)}
														style={{
															backgroundColor: "#ffffff",
															color: "ddd",
															border: "#ddd 1px solid",
															padding: "1px 2px",
															fontSize: "12px",
															fontFamily: "MAN EUROPE_Regular",
															cursor: "pointer",
															display: "flex",
															alignItems: "center",
															height: "20px",
															whiteSpace: "nowrap",
														}}
														title="View more information"
													>
														More info <ChevronRight size={10} />
													</button>
												</div>
												<div style={{ marginBottom: "1px" }}>
													<span style={{ fontFamily: "MAN EUROPE_Regular" }}>
														{row.description}
													</span>
												</div>
												<div style={{ marginBottom: "1px" }}>
													<span
														style={{
															fontWeight: "600",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														Project:
													</span>{" "}
													<span style={{ fontFamily: "MAN EUROPE_Regular" }}>
														{row.project}
													</span>
												</div>
												<div style={{ marginBottom: "1px" }}>
													<span
														style={{
															fontWeight: "600",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														Change no:
													</span>{" "}
													<span style={{ fontFamily: "MAN EUROPE_Regular" }}>
														{row.changeNo}
													</span>
												</div>
												<div style={{ marginBottom: "-12px" }}>
													<span
														style={{
															fontWeight: "600",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														Index type:
													</span>{" "}
													<span style={{ fontFamily: "MAN EUROPE_Regular" }}>
														{row.wf_index || ""}
													</span>
												</div>
											</div>
										</td>
										<td
											colSpan="3"
											style={{
												...styles.td,
												position: "relative",
												padding: "10px 6px 3px 4px",
												verticalAlign: "top",
												borderBottom: "none",
												pointerEvents: "none",
											}}
										>
											<div
												data-tour-target="serie-milestone-row"
												style={{
													position: "relative",
													height: "16px",
													display: "flex",
													alignItems: "center",
													marginTop: "0px",
													marginBottom: "4px",
													pointerEvents: "auto",
												}}
											>
												<button
													data-tour-target="serie-quality-modal"
													onClick={() => handleViewMilestones(row)}
													style={{
														...styles.viewMoreButton,
														left: "calc(0% - 25px)",
														top: "50%",
														transform: "translateY(-50%)",
													}}
													title="View detailed milestone information"
												>
													<ChevronDown size={10} />
												</button>
												<div
													style={{
														position: "absolute",
														top: "50%",
														left: "0",
														right: "0",
														height: "2px",
														backgroundColor: "#000",
														transform: "translateY(-50%)",
														zIndex: 1,
													}}
												></div>{" "}
												{/* Green line from FET to A80 when A80 status is OK */}
												{row.milestone_status?.a80_status?.toUpperCase() ===
													"OK" && (
													<div
														style={{
															position: "absolute",
															top: "50%",
															left: "5.75%",
															width: "13%",
															height: "4px",
															backgroundColor: "#28a745",
															zIndex: 1,
															transform: "translateY(-50%)",
														}}
													></div>
												)}
												{row.milestone_status?.a80_status?.toUpperCase() ===
													"OK" &&
													row.milestone_status?.e99_status?.toUpperCase() ===
														"OK" && (
														<div
															style={{
																position: "absolute",
																top: "50%",
																left: "17.66%",
																width: "13%",
																height: "4px",
																backgroundColor: "#28a745",
																zIndex: 1,
																transform: "translateY(-50%)",
															}}
														></div>
													)}{" "}
												{row.project === "P103988" ? (
													/* P103988: E99 → P99 → LR99 → MR99 (no QI100/QI500/QI900) */
													<>
														{/* E99 → P99: slots 3→4 */}
														{row.milestone_status?.e99_status?.toUpperCase() ===
															"OK" &&
															row.milestone_status?.p99_status?.toUpperCase() ===
																"OK" && (
																<div
																	style={{
																		position: "absolute",
																		top: "50%",
																		left: "31.33%",
																		width: "13%",
																		height: "4px",
																		backgroundColor: "#28a745",
																		zIndex: 1,
																		transform: "translateY(-50%)",
																	}}
																></div>
															)}
														{/* P99 → LR99: slots 4→5 */}
														{row.milestone_status?.p99_status?.toUpperCase() ===
															"OK" &&
															row.milestone_status?.lr99_status?.toUpperCase() ===
																"OK" && (
																<div
																	style={{
																		position: "absolute",
																		top: "50%",
																		left: "43%",
																		width: "13%",
																		height: "4px",
																		backgroundColor: "#28a745",
																		zIndex: 1,
																		transform: "translateY(-50%)",
																	}}
																></div>
															)}
														{/* LR99 → MR99: slots 5→6 */}
														{row.milestone_status?.lr99_status?.toUpperCase() ===
															"OK" &&
															row.milestone_status?.mr99_status?.toUpperCase() ===
																"OK" && (
																<div
																	style={{
																		position: "absolute",
																		top: "50%",
																		left: "55.50%",
																		width: "13%",
																		height: "4px",
																		backgroundColor: "#28a745",
																		zIndex: 1,
																		transform: "translateY(-50%)",
																	}}
																></div>
															)}
													</>
												) : (
													/* Default: E99 → QI100 → QI500 → QI900 → P99 */
													<>
														{row.milestone_status?.e99_status?.toUpperCase() ===
															"OK" &&
															row.milestone_status?.qi100_status?.toUpperCase() ===
																"OK" && (
																<div
																	style={{
																		position: "absolute",
																		top: "50%",
																		left: "31.33%",
																		width: "13%",
																		height: "4px",
																		backgroundColor: "#28a745",
																		zIndex: 1,
																		transform: "translateY(-50%)",
																	}}
																></div>
															)}
														{row.milestone_status?.qi100_status?.toUpperCase() ===
															"OK" &&
															row.milestone_status?.qi500_status?.toUpperCase() ===
																"OK" && (
																<div
																	style={{
																		position: "absolute",
																		top: "50%",
																		left: "43%",
																		width: "13%",
																		height: "4px",
																		backgroundColor: "#28a745",
																		zIndex: 1,
																		transform: "translateY(-50%)",
																	}}
																></div>
															)}
														{row.milestone_status?.qi500_status?.toUpperCase() ===
															"OK" &&
															row.milestone_status?.qi900_status?.toUpperCase() ===
																"OK" && (
																<div
																	style={{
																		position: "absolute",
																		top: "50%",
																		left: "55.50%",
																		width: "13%",
																		height: "4px",
																		backgroundColor: "#28a745",
																		zIndex: 1,
																		transform: "translateY(-50%)",
																	}}
																></div>
															)}
														{row.milestone_status?.qi900_status?.toUpperCase() ===
															"OK" &&
															row.milestone_status?.p99_status?.toUpperCase() ===
																"OK" && (
																<div
																	style={{
																		position: "absolute",
																		top: "50%",
																		left: "68.33%",
																		width: "13%",
																		height: "4px",
																		backgroundColor: "#28a745",
																		zIndex: 1,
																		transform: "translateY(-50%)",
																	}}
																></div>
															)}
													</>
												)}
												<div
													style={{
														position: "relative",
														width: "100%",
														display: "flex",
														justifyContent: "space-between",
														alignItems: "center",
														zIndex: 2,
													}}
												>
													{/* FET */}
													<div
														style={{
															flex: "0 0 12.5%",
															display: "flex",
															flexDirection: "column",
															alignItems: "center",
															overflow: "hidden",
														}}
													>
														<span
															style={{
																fontSize: "9px",
																color: "black",
																marginBottom: "0px",
																fontFamily: "MAN EUROPE_Bold",
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
																maxWidth: "100%",
																textAlign: "center",
															}}
														>
															FET
														</span>
														<div
															style={{
																flexShrink: 0,
																width: "7px",
																height: "7px",
																borderRadius: "50%",
																backgroundColor: "#666",
																border: "1px solid #333",
															}}
														></div>{" "}
														<span
															style={{
																fontSize: "11px",
																fontWeight: "500",
																color: "black",
																marginTop: "0px",
																fontFamily: "MAN EUROPE_Regular",
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
																maxWidth: "100%",
																textAlign: "center",
															}}
														>
															{row.milestones?.fet || "-"}
														</span>
													</div>{" "}
													{/* A80 */}
													<div
														title="A80 - Change Management"
														style={{
															flex: "0 0 12.5%",
															display: "flex",
															flexDirection: "column",
															alignItems: "center",
															overflow: "hidden",
														}}
													>
														<span
															style={{
																fontSize: "9px",
																color: "#333",
																marginBottom: "0px",
																fontFamily: "MAN EUROPE_Regular",
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
																maxWidth: "100%",
																textAlign: "center",
															}}
														>
															A80
														</span>
														<div
															style={{
																flexShrink: 0,
																width: "8px",
																height: "8px",
																borderRadius: "50%",
																backgroundColor: getMilestoneColor(
																	row.milestone_status?.a80_status,
																),
																border: `1px solid ${getMilestoneBorderColor(row.milestone_status?.a80_status)}`,
															}}
														></div>
														<span
															style={{
																fontSize: "11px",
																fontWeight: "500",
																color: "black",
																marginTop: "0px",
																fontFamily: "MAN EUROPE_Regular",
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
																maxWidth: "100%",
																textAlign: "center",
															}}
														>
															{row.milestones?.a80 || "-"}
														</span>
													</div>{" "}
													{/* E99 */}
													<div
														title="E99 - Purchasing"
														style={{
															flex: "0 0 12.5%",
															display: "flex",
															flexDirection: "column",
															alignItems: "center",
															overflow: "hidden",
														}}
													>
														<span
															style={{
																fontSize: "9px",
																color: "#333",
																marginBottom: "0px",
																fontFamily: "MAN EUROPE_Regular",
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
																maxWidth: "100%",
																textAlign: "center",
															}}
														>
															E99
														</span>
														<div
															style={{
																flexShrink: 0,
																width: "8px",
																height: "8px",
																borderRadius: "50%",
																backgroundColor: getMilestoneColor(
																	row.milestone_status?.e99_status,
																),
																border: `1px solid ${getMilestoneBorderColor(row.milestone_status?.e99_status)}`,
															}}
														></div>
														<span
															style={{
																fontSize: "11px",
																fontWeight: "500",
																color: "black",
																marginTop: "0px",
																fontFamily: "MAN EUROPE_Regular",
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
																maxWidth: "100%",
																textAlign: "center",
															}}
														>
															{row.milestones?.e99 || "-"}
														</span>{" "}
													</div>{" "}
													{row.project === "P103988" ? (
														/* P103988 layout: FET · A80 · E99 · P99 · LR99 · MR99 · [spacer] · PET
                             QI100/QI500/QI900 are replaced by P99, LR99, MR99.
                             Use flex: '0 0 16.66%' for the three new milestones so they fill
                             the three vacated QI slots; the spacer keeps PET at the far right. */
														<>
															{" "}
															{/* P99 — takes first of the three reclaimed QI slots */}
															<div
																title="P99 - Disposition"
																style={{
																	flex: "0 0 12.5%",
																	display: "flex",
																	flexDirection: "column",
																	alignItems: "center",
																	overflow: "hidden",
																}}
															>
																<span
																	style={{
																		fontSize: "9px",
																		color: "#333",
																		marginBottom: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	P99
																</span>
																<div
																	style={{
																		flexShrink: 0,
																		width: "8px",
																		height: "8px",
																		borderRadius: "50%",
																		backgroundColor: getMilestoneColor(
																			row.milestone_status?.p99_status,
																		),
																		border: `1px solid ${getMilestoneBorderColor(row.milestone_status?.p99_status)}`,
																	}}
																></div>
																<span
																	style={{
																		fontSize: "11px",
																		fontWeight: "500",
																		color: "black",
																		marginTop: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	{row.milestones?.p99 || "-"}
																</span>
															</div>{" "}
															{/* LR99 */}
															<div
																title="LR99 - Logistics"
																style={{
																	flex: "0 0 12.5%",
																	display: "flex",
																	flexDirection: "column",
																	alignItems: "center",
																	overflow: "hidden",
																}}
															>
																<span
																	style={{
																		fontSize: "9px",
																		color: "#333",
																		marginBottom: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	LR99
																</span>
																<div
																	style={{
																		flexShrink: 0,
																		width: "8px",
																		height: "8px",
																		borderRadius: "50%",
																		backgroundColor: getMilestoneColor(
																			row.milestone_status?.lr99_status,
																		),
																		border: `1px solid ${getMilestoneBorderColor(row.milestone_status?.lr99_status)}`,
																	}}
																></div>
																<span
																	style={{
																		fontSize: "11px",
																		fontWeight: "500",
																		color: "black",
																		marginTop: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	{row.milestones?.lr99
																		? `I: ${row.milestones.lr99}`
																		: "-"}
																</span>
															</div>{" "}
															{/* MR99 */}
															<div
																title="MR99 - Assembly Planning"
																style={{
																	flex: "0 0 12.5%",
																	display: "flex",
																	flexDirection: "column",
																	alignItems: "center",
																	overflow: "hidden",
																}}
															>
																<span
																	style={{
																		fontSize: "9px",
																		color: "#333",
																		marginBottom: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	MR99
																</span>
																<div
																	style={{
																		flexShrink: 0,
																		width: "8px",
																		height: "8px",
																		borderRadius: "50%",
																		backgroundColor: getMilestoneColor(
																			row.milestone_status?.mr99_status,
																		),
																		border: `1px solid ${getMilestoneBorderColor(row.milestone_status?.mr99_status)}`,
																	}}
																></div>
																<span
																	style={{
																		fontSize: "11px",
																		fontWeight: "500",
																		color: "black",
																		marginTop: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	{row.milestones?.mr99
																		? `I: ${row.milestones.mr99}`
																		: "-"}
																</span>
															</div>
															{/* Spacer to push PET to its correct far-right position */}
															<div style={{ flex: "0 0 12.5%" }}></div>
														</>
													) : (
														/* Default layout: QI100 → QI500 → QI900 → P99 */
														<>
															{" "}
															{/* QI100 */}
															<div
																title="QI100 - Quality"
																style={{
																	flex: "0 0 12.5%",
																	display: "flex",
																	flexDirection: "column",
																	alignItems: "center",
																	overflow: "hidden",
																}}
															>
																<span
																	style={{
																		fontSize: "9px",
																		color: "#333",
																		marginBottom: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	QI100
																</span>
																<div
																	style={{
																		flexShrink: 0,
																		width: "8px",
																		height: "8px",
																		borderRadius: "50%",
																		backgroundColor: getMilestoneColor(
																			row.milestone_status?.qi100_status,
																		),
																		border: `1px solid ${getMilestoneBorderColor(row.milestone_status?.qi100_status)}`,
																	}}
																></div>
																<span
																	style={{
																		fontSize: "11px",
																		fontWeight: "500",
																		color: "black",
																		marginTop: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	{row.milestones?.qi100 || "-"}
																</span>
															</div>{" "}
															{/* QI500 */}
															<div
																title="QI500 - Quality"
																style={{
																	flex: "0 0 12.5%",
																	display: "flex",
																	flexDirection: "column",
																	alignItems: "center",
																	overflow: "hidden",
																}}
															>
																<span
																	style={{
																		fontSize: "9px",
																		color: "#333",
																		marginBottom: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	QI500
																</span>
																<div
																	style={{
																		flexShrink: 0,
																		width: "8px",
																		height: "8px",
																		borderRadius: "50%",
																		backgroundColor: getMilestoneColor(
																			row.milestone_status?.qi500_status,
																		),
																		border: `1px solid ${getMilestoneBorderColor(row.milestone_status?.qi500_status)}`,
																	}}
																></div>
																<span
																	style={{
																		fontSize: "11px",
																		fontWeight: "500",
																		color: "black",
																		marginTop: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	{row.milestones?.qi500 || "-"}
																</span>
															</div>{" "}
															{/* QI900 */}
															<div
																title="QI900 - Quality"
																style={{
																	flex: "0 0 12.5%",
																	display: "flex",
																	flexDirection: "column",
																	alignItems: "center",
																	overflow: "hidden",
																}}
															>
																<span
																	style={{
																		fontSize: "9px",
																		color: "#333",
																		marginBottom: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	QI900
																</span>
																<div
																	style={{
																		flexShrink: 0,
																		width: "8px",
																		height: "8px",
																		borderRadius: "50%",
																		backgroundColor: getMilestoneColor(
																			row.milestone_status?.qi900_status,
																		),
																		border: `1px solid ${getMilestoneBorderColor(row.milestone_status?.qi900_status)}`,
																	}}
																></div>
																<span
																	style={{
																		fontSize: "11px",
																		fontWeight: "500",
																		color: "black",
																		marginTop: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	{row.milestones?.qi900 || "-"}
																</span>
															</div>{" "}
															{/* Logistics / P99 */}
															<div
																title="P99 - Disposition"
																style={{
																	flex: "0 0 12.5%",
																	display: "flex",
																	flexDirection: "column",
																	alignItems: "center",
																	overflow: "hidden",
																}}
															>
																<span
																	style={{
																		fontSize: "9px",
																		color: "#333",
																		marginBottom: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	P99
																</span>
																<div
																	style={{
																		flexShrink: 0,
																		width: "8px",
																		height: "8px",
																		borderRadius: "50%",
																		backgroundColor: getMilestoneColor(
																			row.milestone_status?.p99_status,
																		),
																		border: `1px solid ${getMilestoneBorderColor(row.milestone_status?.p99_status)}`,
																	}}
																></div>
																<span
																	style={{
																		fontSize: "11px",
																		fontWeight: "500",
																		color: "black",
																		marginTop: "0px",
																		fontFamily: "MAN EUROPE_Regular",
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		maxWidth: "100%",
																		textAlign: "center",
																	}}
																>
																	{row.milestones?.p99 || "-"}
																</span>
															</div>
														</>
													)}
													{/* PET */}
													<div
														style={{
															flex: "0 0 12.5%",
															display: "flex",
															flexDirection: "column",
															alignItems: "center",
															overflow: "hidden",
														}}
													>
														<span
															style={{
																fontSize: "10px",
																color: "black",
																marginBottom: "0px",
																fontFamily: "MAN EUROPE_Bold",
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
																maxWidth: "100%",
																textAlign: "center",
															}}
														>
															PET
														</span>
														<div
															style={{
																flexShrink: 0,
																width: "8px",
																height: "8px",
																borderRadius: "50%",
																backgroundColor: "#666",
																border: "1px solid #333",
															}}
														></div>
														<span
															style={{
																fontSize: "11px",
																fontWeight: "500",
																color: "black",
																marginTop: "0px",
																fontFamily: "MAN EUROPE_Regular",
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
																maxWidth: "100%",
																textAlign: "center",
															}}
														>
															{row.milestones?.pet || "-"}
														</span>
													</div>
												</div>
											</div>
										</td>
										<td
											style={{
												...styles.td,
												verticalAlign: "top",
												borderBottom: "none",
												paddingTop: "4px",
												paddingBottom: "4px",
											}}
										>
											{row.stockStatus &&
											Object.keys(row.stockStatus).length > 0 ? (
												<div
													style={{
														fontSize: "11px",
														fontFamily: "MAN EUROPE_Bold",
													}}
												>
													{Object.entries(row.stockStatus).map(
														([plantKey, plantData]) => (
															<div key={plantKey} style={styles.stockRow}>
																<span style={styles.stockLabel}>
																	{plantKey.charAt(0).toUpperCase()}
																</span>{" "}
																<span style={styles.stockValue}>
																	{plantData.req_dt &&
																	String(plantData.req_dt).toLowerCase() !==
																		"nan"
																		? plantData.req_dt
																		: "-"}
																</span>
																<span style={styles.stockValue}>
																	{plantData.next_req_dt &&
																	String(
																		plantData.next_req_dt,
																	).toLowerCase() !== "nan"
																		? plantData.next_req_dt
																		: "-"}
																</span>
																<span style={styles.stockValue}>
																	{plantData.stock &&
																	String(plantData.stock).toLowerCase() !==
																		"nan"
																		? plantData.stock
																		: "-"}
																</span>
																<div
																	style={{
																		display: "flex",
																		justifyContent: "center",
																		width: "62px",
																	}}
																>
																	<StockStatusSelect
																		value={
																			plantData.status &&
																			String(plantData.status).toLowerCase() !==
																				"nan"
																				? plantData.status
																				: ""
																		}
																		onChange={handleUpdateStockStatus}
																		disabled={
																			updatingStockStatus[
																				`${row.SNR}_${row.project}_${plantKey}`
																			]
																		}
																		plantKey={plantKey}
																		snr={row.SNR}
																		project={row.project}
																	/>
																</div>
															</div>
														),
													)}
												</div>
											) : (
												<div
													style={{
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														height: "40px",
														fontSize: "10px",
														color: "#9ca3af",
														fontFamily: "MAN EUROPE_Regular",
														fontStyle: "italic",
													}}
												>
													No stock data
												</div>
											)}
										</td>
										{/* BTT Status cell */}
										<td
											style={{
												...styles.td,
												verticalAlign: "top",
												borderBottom: "none",
												paddingTop: "4px",
												paddingBottom: "4px",
												width: "190px",
												position: "relative",
											}}
										>
											{(() => {
												const currentBttValue = row.btt_status || "";
												const currentBttValues = currentBttValue
													? currentBttValue
															.split(",")
															.map((v) => v.trim())
															.filter((v) => v !== "")
													: [];
												const tempValues =
													tempBttStatusSelections[itemId] !== undefined
														? tempBttStatusSelections[itemId]
														: currentBttValues;
												return (
													<div
														style={{
															display: "flex",
															flexDirection: "column",
															gap: "2px",
															position: "relative",
														}}
													>
														{" "}
														{/* Display area */}
														<div
															className="btt-status-container"
															style={{
																minHeight: "32px",
																maxHeight: "60px",
																padding: "4px",
																border: updatingBttStatus[itemId]
																	? "1px solid #d1d5db"
																	: "1px solid #e5e7eb",
																borderRadius: "4px",
																backgroundColor: updatingBttStatus[itemId]
																	? "#f9fafb"
																	: "#fff",
																display: "flex",
																flexWrap: "wrap",
																gap: "4px",
																alignItems: updatingBttStatus[itemId]
																	? "center"
																	: "flex-start",
																justifyContent: updatingBttStatus[itemId]
																	? "center"
																	: "flex-start",
																cursor: updatingBttStatus[itemId]
																	? "default"
																	: "pointer",
																overflowY: "auto",
																scrollbarWidth: "thin",
																scrollbarColor: "#d1d5db transparent",
																position: "relative",
															}}
															onClick={() => {
																if (updatingBttStatus[itemId]) return; // ignore clicks while loading
																const dropdownId = `btt-status-dropdown-${itemId}`;
																const dropdown =
																	document.getElementById(dropdownId);
																if (dropdown) {
																	const isVisible =
																		dropdown.style.display !== "none";
																	if (isVisible) {
																		dropdown.style.display = "none";
																	} else {
																		const container = dropdown.parentElement;
																		if (container) {
																			const rect =
																				container.getBoundingClientRect();
																			const spaceBelow =
																				window.innerHeight - rect.bottom;
																			if (spaceBelow < 240) {
																				dropdown.style.top = "auto";
																				dropdown.style.bottom = "100%";
																				dropdown.style.marginBottom = "2px";
																				dropdown.style.marginTop = "";
																			} else {
																				dropdown.style.top = "100%";
																				dropdown.style.bottom = "auto";
																				dropdown.style.marginTop = "2px";
																				dropdown.style.marginBottom = "";
																			}
																		}
																		dropdown.style.display = "block";
																	}
																}
															}}
														>
															{" "}
															{updatingBttStatus[itemId] ? (
																/* Full-box loading state — shown while a chip removal or add is in flight */
																<Box
																	sx={{
																		display: "flex",
																		alignItems: "center",
																		gap: "6px",
																	}}
																>
																	<CircularProgress
																		size={14}
																		sx={{ color: "#cc0000" }}
																	/>
																	<Typography
																		sx={{
																			fontSize: "11px",
																			color: "#6b7280",
																			fontFamily: "MAN EUROPE_Regular",
																		}}
																	>
																		Updating...
																	</Typography>
																</Box>
															) : currentBttValues.length === 0 ? (
																<span
																	style={{
																		color: "#9ca3af",
																		fontSize: "11px",
																		fontFamily: "MAN EUROPE_Regular",
																	}}
																>
																	Click to select...
																</span>
															) : (
																currentBttValues.map((val, i) => (
																	<div
																		key={i}
																		style={{
																			backgroundColor: "black",
																			color: "white",
																			fontSize: "11px",
																			fontFamily: "MAN EUROPE_Bold",
																			padding: "2px 4px",
																			borderRadius: "3px",
																			display: "flex",
																			alignItems: "center",
																			gap: "3px",
																			maxWidth: "100%",
																			minHeight: "18px",
																		}}
																		title={val}
																	>
																		<span
																			style={{
																				overflow: "hidden",
																				textOverflow: "ellipsis",
																				whiteSpace: "nowrap",
																				maxWidth: "110px",
																			}}
																		>
																			{val}
																		</span>
																		<button
																			onClick={(e) => {
																				e.stopPropagation();
																				const chipKey = `${row.SNR}_${row.project}`;
																				const newVals = currentBttValues.filter(
																					(v) => v !== val,
																				);
																				setRemovingBttChip((prev) => ({
																					...prev,
																					[chipKey]: val,
																				}));
																				handleBttStatusUpdate(
																					row.SNR,
																					row.project,
																					newVals,
																				);
																			}}
																			style={{
																				background: "none",
																				border: "none",
																				color: "white",
																				cursor: "pointer",
																				fontSize: "12px",
																				padding: "0",
																				lineHeight: "1",
																				width: "14px",
																				height: "14px",
																				display: "flex",
																				alignItems: "center",
																				justifyContent: "center",
																			}}
																			title="Remove"
																		>
																			×
																		</button>
																	</div>
																))
															)}
														</div>
														{/* Dropdown */}
														<div
															id={`btt-status-dropdown-${itemId}`}
															style={{
																position: "absolute",
																top: "100%",
																left: "0",
																right: "0",
																backgroundColor: "white",
																border: "1px solid #e5e7eb",
																borderRadius: "4px",
																boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
																zIndex: 99999,
																maxHeight: "220px",
																overflowY: "auto",
																display: "none",
															}}
														>
															<div
																style={{
																	maxHeight: "150px",
																	overflowY: "auto",
																}}
															>
																{STATUS_BTT_OPTIONS.map((option) => {
																	const isSelected =
																		tempValues.includes(option);
																	return (
																		<div
																			key={option}
																			style={{
																				padding: "7px 10px",
																				fontSize: "12px",
																				cursor: "pointer",
																				backgroundColor: isSelected
																					? "#f3f4f6"
																					: "white",
																				borderBottom: "1px solid #f1f5f9",
																				display: "flex",
																				alignItems: "center",
																				gap: "8px",
																				minHeight: "30px",
																				fontFamily: "MAN EUROPE_Regular",
																			}}
																			onClick={() => {
																				const newTempValues = isSelected
																					? tempValues.filter(
																							(v) => v !== option,
																						)
																					: [...tempValues, option];
																				setTempBttStatusSelections((prev) => ({
																					...prev,
																					[itemId]: newTempValues,
																				}));
																			}}
																			onMouseEnter={(e) => {
																				e.currentTarget.style.backgroundColor =
																					"#f8f9fa";
																			}}
																			onMouseLeave={(e) => {
																				e.currentTarget.style.backgroundColor =
																					isSelected ? "#f3f4f6" : "white";
																			}}
																		>
																			<input
																				type="checkbox"
																				checked={isSelected}
																				readOnly
																				style={{
																					width: "13px",
																					height: "13px",
																					accentColor: "#cc0000",
																				}}
																			/>
																			<span style={{ fontWeight: "500" }}>
																				{option}
																			</span>
																		</div>
																	);
																})}
															</div>
															{/* Action buttons */}
															<div
																style={{
																	borderTop: "1px solid #f1f5f9",
																	padding: "6px 8px",
																	display: "flex",
																	gap: "6px",
																	justifyContent: "flex-end",
																	backgroundColor: "#f8f9fa",
																}}
															>
																<button
																	onClick={() => {
																		setTempBttStatusSelections((prev) => {
																			const s = { ...prev };
																			delete s[itemId];
																			return s;
																		});
																		const dropdown = document.getElementById(
																			`btt-status-dropdown-${itemId}`,
																		);
																		if (dropdown)
																			dropdown.style.display = "none";
																	}}
																	style={{
																		padding: "5px 10px",
																		fontSize: "12px",
																		border: "1px solid #d1d5db",
																		borderRadius: "3px",
																		backgroundColor: "white",
																		color: "#666",
																		cursor: "pointer",
																		fontFamily: "MAN EUROPE_Regular",
																	}}
																>
																	Cancel
																</button>
																<button
																	onClick={async () => {
																		const newValues =
																			tempBttStatusSelections[itemId] !==
																			undefined
																				? tempBttStatusSelections[itemId]
																				: currentBttValues;
																		await handleBttStatusUpdate(
																			row.SNR,
																			row.project,
																			newValues,
																		);
																		setTempBttStatusSelections((prev) => {
																			const s = { ...prev };
																			delete s[itemId];
																			return s;
																		});
																		const dropdown = document.getElementById(
																			`btt-status-dropdown-${itemId}`,
																		);
																		if (dropdown)
																			dropdown.style.display = "none";
																	}}
																	disabled={updatingBttStatus[itemId]}
																	style={{
																		padding: "5px 10px",
																		fontSize: "12px",
																		border: "none",
																		borderRadius: "3px",
																		backgroundColor: updatingBttStatus[itemId]
																			? "#ccc"
																			: "#cc0000",
																		color: "white",
																		cursor: updatingBttStatus[itemId]
																			? "not-allowed"
																			: "pointer",
																		fontFamily: "MAN EUROPE_Bold",
																		display: "flex",
																		alignItems: "center",
																		gap: "4px",
																	}}
																>
																	{updatingBttStatus[itemId] ? (
																		<>
																			<div
																				style={{
																					width: "10px",
																					height: "10px",
																					border: "1px solid white",
																					borderTop: "1px solid transparent",
																					borderRadius: "50%",
																					animation: "spin 1s linear infinite",
																				}}
																			/>
																			Saving...
																		</>
																	) : (
																		"Save"
																	)}
																</button>
															</div>
														</div>
													</div>
												);
											})()}
										</td>
										{/* Status column */}
										<td
											style={{
												...styles.td,
												verticalAlign: "top",
												borderBottom: "none",
												paddingTop: "4px",
												paddingBottom: "4px",
											}}
										>
											<LocalizationProvider dateAdapter={AdapterDateFns}>
												<div style={{ display: "flex", gap: "6px" }}>
													<div style={{ flex: 1 }}>
														<div
															data-tour-target="serie-resp-field"
															style={{
																display: "flex",
																alignItems: "center",
																gap: "2px",
																marginBottom: "1px",
															}}
														>
															<span
																style={{
																	fontSize: "12px",
																	fontFamily: "MAN EUROPE_Regular",
																	minWidth: "35px",
																	color: "black",
																}}
															>
																Resp:
															</span>
															{isGlobalEditMode ? (
																<div
																	style={{
																		flex: 1,
																		padding: "2px 3px",
																		border: "1px solid #d1d5db",
																		borderRadius: "2px",
																		fontSize: "11px",
																		fontStyle: "bold",
																		fontWeight: "600",
																		backgroundColor: "white",
																		height: "16px",
																		display: "flex",
																		justifyContent: "space-between",
																		alignItems: "center",
																		position: "relative",
																	}}
																>
																	<TextField
																		variant="outlined"
																		size="small"
																		value={
																			responsibleValues[
																				`${row.SNR}_${row.project}`
																			] !== undefined
																				? responsibleValues[
																						`${row.SNR}_${row.project}`
																					]
																				: row.status.responsible === "<NA>"
																					? ""
																					: row.status.responsible || ""
																		}
																		onChange={(e) => {
																			const newValue = e.target.value.replace(
																				/[^a-zA-Z0-9\s.\-_'/]/g,
																				"",
																			);
																			setResponsibleValues((prev) => ({
																				...prev,
																				[`${row.SNR}_${row.project}`]: newValue,
																			}));
																		}}
																		placeholder="Enter name"
																		sx={{
																			"& .MuiOutlinedInput-root": {
																				fontSize: "15px",
																				height: "24px",
																				fontStyle: "bold",
																				fontFamily: "MAN EUROPE_Regular",
																				border: "none",
																				"& fieldset": { border: "none" },
																				"&:hover fieldset": { border: "none" },
																				"&.Mui-focused fieldset": {
																					border: "none",
																				},
																			},
																			"& .MuiOutlinedInput-input": {
																				padding: "0px 4px",
																				fontSize: "15px",
																			},
																		}}
																	/>{" "}
																	{responsibleValues[
																		`${row.SNR}_${row.project}`
																	] && (
																		<X
																			size={16}
																			style={{
																				color: "#dc2626",
																				cursor: "pointer",
																				marginLeft: "4px",
																				flexShrink: 0,
																			}}
																			onClick={() => {
																				setResponsibleValues((prev) => ({
																					...prev,
																					[`${row.SNR}_${row.project}`]: "",
																				}));
																			}}
																			title="Clear responsible"
																		/>
																	)}
																</div>
															) : (
																<div
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: "2px",
																	}}
																>
																	<span
																		style={{
																			width: "100px",
																			padding: "2px 4px",
																			fontSize: "11px",
																			border: "1px solid #d1d5db",
																			color: "black",
																			backgroundColor: "#f3f4f6",
																			borderRadius: "3px",
																			fontStyle: "bold",
																			fontFamily: "MAN EUROPE_Regular",
																			fontWeight: "600",
																			textAlign: "center",
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																			marginLeft: "30px",
																		}}
																	>
																		{row.status.responsible || "-"}
																	</span>
																</div>
															)}
														</div>
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: "2px",
																marginBottom: "1px",
															}}
														>
															<span
																style={{
																	fontSize: "12px",
																	fontFamily: "MAN EUROPE_Regular",
																	minWidth: "35px",
																	color: "black",
																}}
															>
																Date:
															</span>
															{isGlobalEditMode ? (
																<div
																	style={{
																		flex: 1,
																		padding: "2px 3px",
																		border: "1px solid #d1d5db",
																		borderRadius: "2px",
																		fontSize: "12px",
																		backgroundColor: "white",
																		height: "16px",
																		display: "flex",
																		justifyContent: "space-between",
																		alignItems: "center",
																		position: "relative",
																	}}
																	onClick={() =>
																		setOpenFollowupDatePicker(
																			`${row.SNR}_${row.project}`,
																		)
																	}
																>
																	<span
																		style={{
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																			flex: "1",
																			fontSize: "12px",
																			fontFamily: "MAN EUROPE_Regular",
																		}}
																	>
																		{(() => {
																			const dateValue =
																				followupValues[
																					`${row.SNR}_${row.project}`
																				];
																			if (
																				dateValue &&
																				dateValue instanceof Date &&
																				!isNaN(dateValue.getTime())
																			) {
																				try {
																					return format(
																						dateValue,
																						"dd/MM/yyyy",
																					);
																				} catch (error) {
																					console.error(
																						"Error formatting date:",
																						error,
																					);
																					return "Select date";
																				}
																			}
																			return "Select date";
																		})()}
																	</span>
																	<div
																		style={{
																			display: "flex",
																			alignItems: "center",
																			marginLeft: "4px",
																			gap: "4px",
																		}}
																	>
																		<Calendar size={12} />
																		{followupValues[
																			`${row.SNR}_${row.project}`
																		] &&
																			followupValues[
																				`${row.SNR}_${row.project}`
																			] instanceof Date &&
																			!isNaN(
																				followupValues[
																					`${row.SNR}_${row.project}`
																				].getTime(),
																			) && (
																				<X
																					size={10}
																					style={{
																						color: "#dc2626",
																						cursor: "pointer",
																					}}
																					onClick={(e) => {
																						e.stopPropagation();
																						setFollowupValues((prev) => ({
																							...prev,
																							[`${row.SNR}_${row.project}`]:
																								null,
																						}));
																					}}
																					title="Clear date"
																				/>
																			)}
																	</div>
																	{openFollowupDatePicker ===
																		`${row.SNR}_${row.project}` && (
																		<div
																			style={{
																				position: "absolute",
																				zIndex: 10,
																				backgroundColor: "white",
																				padding: "8px",
																				top: "100%",
																				right: 0,
																				boxShadow:
																					"0 4px 6px rgba(0, 0, 0, 0.1)",
																				borderRadius: "4px",
																			}}
																		>
																			<DatePicker
																				open={true}
																				value={
																					followupValues[
																						`${row.SNR}_${row.project}`
																					] || null
																				}
																				onChange={(newValue) => {
																					setFollowupValues((prev) => ({
																						...prev,
																						[`${row.SNR}_${row.project}`]:
																							newValue,
																					}));
																					setOpenFollowupDatePicker(null);
																				}}
																				onClose={() =>
																					setOpenFollowupDatePicker(null)
																				}
																				slotProps={{
																					textField: {
																						size: "small",
																						sx: {
																							width: "140px",
																							"& .MuiInputBase-root": {
																								fontSize: "12px",
																								height: "32px",
																							},
																							"& .MuiInputBase-input": {
																								fontSize: "12px",
																								padding: "6px 8px",
																							},
																						},
																					},
																				}}
																			/>
																		</div>
																	)}
																</div>
															) : (
																<div
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: "2px",
																	}}
																>
																	<span
																		style={{
																			width: "100px",
																			padding: "2px 4px",
																			fontSize: "11px",
																			border: "1px solid #d1d5db",
																			borderRadius: "3px",
																			fontFamily: "MAN EUROPE_Regular",
																			fontWeight: "500",
																			textAlign: "center",
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																			marginLeft: "30px",
																		}}
																	>
																		{(() => {
																			const followUp = row.status.followUp;
																			if (
																				followUp &&
																				followUp !== "-" &&
																				followUp !== "NaN-NaN-NaN"
																			) {
																				return followUp;
																			}
																			return "-";
																		})()}
																	</span>
																</div>
															)}{" "}
														</div>
														{/* Comment icon (left) + Status toggle (right) — no labels */}
														<div
															style={{
																display: "flex",
																justifyContent: "space-between",
																alignItems: "center",
																marginTop: "6px",
															}}
														>
															{/* Comment icon */}
															{(() => {
																const seriesKey = `${row.SNR}_${row.project}`;
																const commentEntry = commentsData[seriesKey];
																const hasComments =
																	!!commentEntry?.latest_comment;
																const latestCommentText =
																	commentEntry?.latest_comment || "";
																const latestCommentUser =
																	commentEntry?.latest_comment_user || "";
																const commentButton = (
																	<button
																		data-tour-target="serie-comment-button"
																		onClick={() => handleOpenCommentModal(row)}
																		style={{
																			background: hasComments
																				? "#1e2832"
																				: "#ddd",
																			border: "none",
																			color: "white",
																			cursor: "pointer",
																			padding: "2px 4px",
																			borderRadius: "2px",
																			display: "flex",
																			alignItems: "center",
																			height: "18px",
																			justifyContent: "center",
																			transition: "background 0.2s ease",
																		}}
																		aria-label="View/Add Comments"
																	>
																		<MessageSquare size={12} />
																	</button>
																);
																if (hasComments && latestCommentText) {
																	return (
																		<CommentTooltip
																			commentText={latestCommentText}
																			user={latestCommentUser}
																			title="Latest Series Comment"
																		>
																			{commentButton}
																		</CommentTooltip>
																	);
																}
																return commentButton;
															})()}
															{/* Status toggle */}
															<div
																data-tour-target="serie-status-toggle"
																style={{
																	display: "flex",
																	alignItems: "center",
																}}
															>
																{updatingStatusToggle[
																	`${row.SNR}_${row.project}`
																] ? (
																	<CircularProgress
																		size={12}
																		thickness={5}
																		sx={{ color: "#cc0000" }}
																	/>
																) : (
																	<ToggleSwitch
																		value={row.status_toggle}
																		onChange={() =>
																			handleStatusToggle(
																				row.SNR,
																				row.project,
																				row.status_toggle,
																			)
																		}
																		disabled={!canWrite}
																		size="small"
																	/>
																)}
															</div>
														</div>
													</div>
												</div>
											</LocalizationProvider>{" "}
										</td>
									</tr>
									<tr
										style={{
											backgroundColor: idx % 2 === 0 ? "#fff" : "#fafbfc",
										}}
									>
										{/* Empty cell for checkbox column */}
										<td
											style={{
												...styles.td,
												textAlign: "center",
												height: "10px",
												borderLeft:
													(isGlobalEditMode
														? flaggingValues[itemId]
														: row.flagging) === "Y"
														? "3px solid #ff6b35"
														: "3px solid transparent",
											}}
										></td>
										<td
											style={{
												...styles.td,
												textAlign: "center",
												height: "10px",
											}}
										></td>
										<td
											style={{
												...styles.td,
												textAlign: "left",
												height: "10px",
											}}
										></td>
										<td
											style={{
												...styles.td,
												textAlign: "left",
												padding: "8px 2px 2px 4px",
												position: "relative",
												zIndex: 2,
											}}
										>
											<div
												style={{
													lineHeight: "1.0",
													marginTop: "-50px",
													userSelect: "text",
												}}
											>
												<div style={{ marginBottom: "1px" }}>
													<span
														style={{
															fontWeight: "600",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														Supplier:
													</span>{" "}
													<span style={{ fontFamily: "MAN EUROPE_Regular" }}>
														{row.supplier}
													</span>
												</div>
												<div style={{ marginBottom: "1px" }}>
													<span
														style={{
															fontWeight: "600",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														Purchase type:
													</span>{" "}
													<span style={{ fontFamily: "MAN EUROPE_Regular" }}>
														{row.purchaseType}
													</span>
												</div>
												{/* <div style={{marginBottom: '1px'}}><span style={{fontWeight: '600', fontFamily: 'MAN EUROPE_Bold'}}>Procurement responsible:</span> <span style={{fontFamily: 'MAN EUROPE_Regular'}}>{row.procResponsible}</span></div> */}
											</div>
										</td>
										<td
											style={{
												...styles.td,
												textAlign: "left",
												padding: "8px 2px 2px 4px",
												position: "relative",
												zIndex: 2,
											}}
										>
											<div
												style={{
													lineHeight: "1.0",
													marginTop: "-50px",
													userSelect: "text",
												}}
											>
												<div style={{ marginBottom: "1px" }}>
													<span
														style={{
															fontWeight: "600",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														PPAP Status:
													</span>{" "}
													<span style={{ fontFamily: "MAN EUROPE_Regular" }}>
														{row.deviations && row.deviations[0]
															? row.deviations[0].value
															: ""}
													</span>
												</div>
												<div style={{ marginBottom: "1px" }}>
													<span
														style={{
															fontWeight: "600",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														Special release needed:
													</span>{" "}
													<span style={{ fontFamily: "MAN EUROPE_Regular" }}>
														{row.deviations && row.deviations[1]
															? row.deviations[1].value
															: ""}
													</span>
												</div>
												<div style={{ marginBottom: "1px" }}>
													<span
														style={{
															fontWeight: "600",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														Special release until:
													</span>{" "}
													<span style={{ fontFamily: "MAN EUROPE_Regular" }}>
														{row.deviations && row.deviations[2]
															? row.deviations[2].value
															: ""}
													</span>
												</div>
												{/* <div><span style={{fontWeight: '600', fontFamily: 'MAN EUROPE_Bold'}}>Quality responsible:</span> <span style={{fontFamily: 'MAN EUROPE_Regular'}}>{row.deviations && row.deviations[3] ? row.deviations[3].value : ''}</span></div> */}
											</div>
										</td>
										{/* Date */}
										<td
											style={{
												...styles.td,
												textAlign: "left",
												padding: "8px 2px 2px 4px",
												position: "relative",
												zIndex: 2,
											}}
										>
											<div
												style={{
													lineHeight: "1.0",
													marginTop: "-50px",
													userSelect: "text",
												}}
											>
												{row.dates &&
													row.dates.map((date, i) => (
														<div key={i} style={{ marginBottom: "1px" }}>
															<span
																style={{
																	fontWeight: "600",
																	fontFamily: "MAN EUROPE_Bold",
																}}
															>
																{date.label}:
															</span>{" "}
															<span
																style={{ fontFamily: "MAN EUROPE_Regular" }}
															>
																{date.value}
															</span>
														</div>
													))}
											</div>
										</td>
										<td style={{ ...styles.td, height: "10px" }}></td>
										{/* BTT Status empty sub-row cell */}
										<td style={{ ...styles.td, height: "10px" }}></td>
										<td style={{ ...styles.td, height: "10px" }}></td>
									</tr>
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
				{isLoadingMore && (
					<div
						style={{
							textAlign: "center",
							padding: "15px",
							borderTop: "1px solid #eee",
							backgroundColor: "#f8f9fa",
						}}
					>
						<CircularProgress
							size={30}
							thickness={4}
							sx={{ color: "#cc0000" }}
						/>
						<Typography
							variant="body2"
							style={{
								marginTop: "8px",
								color: "#666",
								fontFamily: "MAN EUROPE_Regular",
							}}
						>
							Loading more records...
						</Typography>
					</div>
				)}{" "}
			</div>{" "}
			<div
				style={{
					backgroundColor: "#f5f5f5",
					padding: "6px 12px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					borderTop: "1px solid #ddd",
					boxShadow: "0 -2px 4px rgba(0,0,0,0.05)",
					minHeight: "30px",
					flexShrink: 0,
					zIndex: 5,
				}}
			>
				{" "}
				{/* Left: spacer */}
				<div style={{ flex: 1 }} />
				{/* Center: record count */}
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						flex: 1,
					}}
				>
					<span
						style={{
							fontSize: "12px",
							color: "#666",
							fontFamily: "MAN EUROPE_Bold",
						}}
					>
						Showing {seriesData.length} of {totalRecords} records
					</span>
					{!hasMoreData && seriesData.length > 0 && (
						<span
							style={{
								fontSize: "11px",
								color: "#059669",
								marginTop: "2px",
								fontFamily: "MAN EUROPE_Bold",
							}}
						>
							✅ All records loaded
						</span>
					)}
				</div>
				{/* Right: last DB update */}
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "flex-end",
						flex: 1,
					}}
				>
					<span
						style={{
							fontSize: "11px",
							color: "#666",
							fontFamily: "MAN EUROPE_Regular",
						}}
					>
						Last DB Update (IST)
					</span>
					<span
						style={{
							fontSize: "12px",
							color: "#333",
							fontFamily: "MAN EUROPE_Bold",
							marginTop: "2px",
						}}
					>
						{lastDbUpdate
							? (() => {
									try {
										const date = new Date(lastDbUpdate);
										date.setHours(date.getHours() + 5);
										date.setMinutes(date.getMinutes() + 30);
										const year = date.getFullYear();
										const month = String(date.getMonth() + 1).padStart(2, "0");
										const day = String(date.getDate()).padStart(2, "0");
										const hours = String(date.getHours()).padStart(2, "0");
										const minutes = String(date.getMinutes()).padStart(2, "0");
										const seconds = String(date.getSeconds()).padStart(2, "0");
										return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
									} catch (e) {
										return lastDbUpdate;
									}
								})()
							: "Loading..."}
					</span>
				</div>
			</div>
			<MilestoneDetailModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				rowData={selectedRowData}
				onMilestoneUpdate={handleMilestoneUpdate}
			/>
			{selectedCommentSeries && (
				<SeriesCommentModal
					open={commentModalOpen}
					onClose={handleCloseCommentModal}
					seriesId={selectedCommentSeries.seriesId}
					snr={selectedCommentSeries.snr}
					snrAnlageProjnr={selectedCommentSeries.snrAnlageProjnr}
					onAddComment={handleCommentAdded}
					newComment={newComment}
					setNewComment={setNewComment}
					editingCommentId={editingCommentId}
					setEditingCommentId={setEditingCommentId}
					editText={editText}
					setEditText={setEditText}
					getUserDisplayName={getUserDisplayName}
					preloadedCommentData={
						selectedCommentSeries &&
						commentsData[
							`${selectedCommentSeries.snr}_${selectedCommentSeries.snrAnlageProjnr}`
						]?.latest_comment
							? {
									hasComments: true,
									latestComment: {
										text: commentsData[
											`${selectedCommentSeries.snr}_${selectedCommentSeries.snrAnlageProjnr}`
										].latest_comment,
										user: commentsData[
											`${selectedCommentSeries.snr}_${selectedCommentSeries.snrAnlageProjnr}`
										].latest_comment_user,
										timestamp:
											commentsData[
												`${selectedCommentSeries.snr}_${selectedCommentSeries.snrAnlageProjnr}`
											].latest_comment_date,
									},
								}
							: { hasComments: false, latestComment: null }
					}
				/>
			)}{" "}
			<SerieDataTableSidePanel
				isOpen={sidePanelOpen}
				onClose={handleCloseSidePanel}
				selectedRow={selectedSidePanelRow}
				onTagsUpdated={(snr, project, newTags) => {
					// Update the row in seriesData so the side panel reflects the new tags immediately
					setSeriesData((prev) =>
						prev.map((item) =>
							item.snr === snr && item.snr_anlage_projnr === project
								? { ...item, tags: newTags }
								: item,
						),
					);
					// Also update selectedSidePanelRow so the panel re-renders with new tags
					setSelectedSidePanelRow((prev) =>
						prev && prev.SNR === snr && prev.project === project
							? { ...prev, tags: newTags }
							: prev,
					);
				}}
			/>
			{/* SNR Detail Modal */}
			<Dialog
				open={snrDetailModalOpen}
				onClose={handleCloseSnrDetail}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						borderRadius: "10px",
						overflow: "hidden",
						boxShadow: "0 10px 40px rgba(30,40,50,0.18)",
					},
				}}
			>
				{/* Header */}
				<Box
					sx={{
						backgroundColor: "#1e2832",
						color: "white",
						px: 3,
						py: 2,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						borderBottom: "1px solid rgba(255,255,255,0.1)",
					}}
				>
					<Box display="flex" alignItems="center" gap={2}>
						<Info size={20} />
						<Typography
							variant="h6"
							sx={{ fontWeight: 600, fontSize: "16px", letterSpacing: "0.5px" }}
						>
							SNR Details
						</Typography>
						{snrDetailSnr && (
							<Typography
								variant="body2"
								sx={{ color: "rgba(255,255,255,0.7)", fontSize: "14px" }}
							>
								{snrDetailSnr}
							</Typography>
						)}
					</Box>
					<button
						onClick={handleCloseSnrDetail}
						style={{
							background: "none",
							border: "none",
							color: "white",
							cursor: "pointer",
							padding: "8px",
							borderRadius: "50%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: "18px",
							transition: "all 0.2s ease",
						}}
						onMouseOver={(e) => {
							e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
						}}
						onMouseOut={(e) => {
							e.currentTarget.style.backgroundColor = "transparent";
						}}
					>
						<X size={18} />
					</button>
				</Box>

				{/* Content */}
				<DialogContent sx={{ p: 0, backgroundColor: "#f8f9fa" }}>
					{" "}
					{snrDetailLoading ? (
						<Box
							display="flex"
							flexDirection="column"
							alignItems="center"
							justifyContent="center"
							py={6}
						>
							<CircularProgress sx={{ color: "#cc0000" }} size={40} />
							<Typography
								variant="body2"
								sx={{ mt: 2, color: "#666", fontFamily: "MAN EUROPE_Regular" }}
							>
								Loading details...
							</Typography>
						</Box>
					) : snrDetailData &&
						snrDetailData.found &&
						snrDetailData.data &&
						snrDetailData.data.length > 0 ? (
						<Box sx={{ p: 2, overflowX: "auto" }}>
							{(() => {
								const columns = [
									{ label: "Kennung", key: "kennung" },
									{ label: "Änd.Nr.", key: "aendnr" },
									{ label: "Projektnummer", key: "projektnummer" },
									{ label: "Ziel-FET", key: "zielfet" },
									{ label: "Ziel-PET", key: "zielpet" },
									{ label: "FET", key: "fet" },
									{ label: "PET", key: "pet" },
									{ label: "Benennung kurz", key: "benennungkurz" },
									{ label: "Anlaufgruppe", key: "anlaufgruppe" },
								];
								return (
									<table
										style={{
											width: "100%",
											borderCollapse: "collapse",
											fontSize: "12px",
											fontFamily: "MAN EUROPE_Regular",
											backgroundColor: "white",
											border: "2px solid #1e2832",
											borderRadius: "8px",
											overflow: "hidden",
										}}
									>
										<thead>
											<tr style={{ backgroundColor: "#1e2832" }}>
												{columns.map((col) => (
													<th
														key={col.key}
														style={{
															padding: "10px 12px",
															textAlign: "left",
															color: "white",
															fontFamily: "MAN EUROPE_Bold",
															fontSize: "11px",
															fontWeight: "600",
															whiteSpace: "nowrap",
															borderRight: "1px solid rgba(255,255,255,0.1)",
															letterSpacing: "0.3px",
														}}
													>
														{col.label}
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{snrDetailData.data.map((row, idx) => (
												<tr
													key={idx}
													style={{
														backgroundColor: idx % 2 === 0 ? "#fff" : "#f8f9fa",
														borderBottom: "1px solid #e5e7eb",
													}}
												>
													{columns.map((col) => (
														<td
															key={col.key}
															style={{
																padding: "9px 12px",
																color: "#1e2832",
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
																borderRight: "1px solid #e5e7eb",
																whiteSpace:
																	col.key === "benennungkurz"
																		? "normal"
																		: "nowrap",
																maxWidth:
																	col.key === "benennungkurz"
																		? "220px"
																		: "unset",
																wordBreak:
																	col.key === "benennungkurz"
																		? "break-word"
																		: "normal",
															}}
														>
															{row[col.key] != null &&
															row[col.key] !== "" &&
															row[col.key] !== "None" ? (
																row[col.key]
															) : (
																<span
																	style={{
																		color: "#d1d5db",
																		fontStyle: "italic",
																	}}
																>
																	—
																</span>
															)}
														</td>
													))}
												</tr>
											))}
										</tbody>
									</table>
								);
							})()}
						</Box>
					) : (
						<Box
							display="flex"
							flexDirection="column"
							alignItems="center"
							justifyContent="center"
							py={6}
						>
							<AlertCircle size={40} color="#d1d5db" />
							<Typography
								variant="body1"
								sx={{ mt: 2, fontFamily: "MAN EUROPE_Bold", color: "#4b5563" }}
							>
								No detail data found
							</Typography>
							<Typography
								variant="body2"
								sx={{ color: "#9ca3af", fontSize: "13px", mt: 0.5 }}
							>
								No records found in t_731_kennung_emcos_ii for SNR:{" "}
								{snrDetailSnr}
							</Typography>
						</Box>
					)}
				</DialogContent>

				<DialogActions
					sx={{
						p: 2,
						backgroundColor: "white",
						borderTop: "1px solid #e5e7eb",
						justifyContent: "flex-end",
					}}
				>
					<Button
						onClick={handleCloseSnrDetail}
						variant="contained"
						sx={{
							backgroundColor: "#1e2832",
							color: "white",
							fontFamily: "MAN EUROPE_Bold",
							textTransform: "none",
							fontSize: "13px",
							borderRadius: "5px",
							px: 3,
							"&:hover": { backgroundColor: "#2d3f4f" },
						}}
					>
						Close
					</Button>{" "}
				</DialogActions>
			</Dialog>{" "}
			{/* Concurrent Update Prevention Modal — covers flagging, BTT status, and status toggle cross-blocking */}
			<Dialog
				open={flagUpdateModal.open}
				onClose={() => setFlagUpdateModal((prev) => ({ ...prev, open: false }))}
				maxWidth="sm"
				fullWidth
				PaperProps={{ style: { borderRadius: "12px", overflow: "hidden" } }}
			>
				<DialogTitle
					sx={{
						backgroundColor: "#1e2832",
						color: "white",
						fontFamily: "MAN EUROPE_Bold",
						fontSize: "16px",
						textAlign: "center",
						py: 2,
					}}
				>
					⚠️ Update Already in Progress
				</DialogTitle>
				<DialogContent sx={{ py: 3, px: 3 }}>
					<DialogContentText
						sx={{
							color: "#333",
							fontSize: "14px",
							textAlign: "center",
							mt: 2,
							mb: 2,
							fontFamily: "MAN EUROPE_Regular",
						}}
					>
						{flagUpdateModal.message}
					</DialogContentText>
					<Box
						sx={{
							backgroundColor: "#fff3cd",
							padding: "12px",
							borderRadius: "6px",
							border: "1px solid #ffeaa7",
							textAlign: "center",
						}}
					>
						<Typography
							variant="body2"
							sx={{
								fontSize: "12px",
								color: "#856404",
								fontFamily: "MAN EUROPE_Bold",
								mb: 0.5,
							}}
						>
							{flagUpdateModal.blockedBy === "btt"
								? `Currently updating: ${Object.keys(updatingBttStatus).length} BTT item(s)`
								: flagUpdateModal.blockedBy === "status"
									? `Currently updating: ${Object.keys(updatingStatusToggle).length} status item(s)`
									: `Currently updating: ${Object.keys(updatingFlagging).length} flag item(s)`}
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontSize: "11px",
								color: "#666",
								fontFamily: "MAN EUROPE_Regular",
							}}
						>
							This dialog will auto-dismiss when the operation completes.
						</Typography>
					</Box>
				</DialogContent>
				<DialogActions
					sx={{
						p: 2,
						justifyContent: "center",
						borderTop: "1px solid #e9ecef",
					}}
				>
					<Button
						onClick={() =>
							setFlagUpdateModal((prev) => ({ ...prev, open: false }))
						}
						variant="outlined"
						sx={{
							textTransform: "none",
							color: "black",
							borderColor: "#d1d5db",
							fontSize: "13px",
							minWidth: "140px",
							fontFamily: "MAN EUROPE_Bold",
							"&:hover": {
								borderColor: "#1e2832",
								backgroundColor: "rgba(30,40,50,0.04)",
							},
						}}
					>
						Wait for Completion
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={showAccessDeniedModal}
				onClose={() => setShowAccessDeniedModal(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ style: { borderRadius: "5px", padding: "8px" } }}
			>
				<DialogContent
					style={{ textAlign: "center", padding: "32px 24px 24px 24px" }}
				>
					<div
						style={{
							width: "64px",
							height: "64px",
							backgroundColor: "#1e2832",
							borderRadius: "50%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							margin: "0 auto 24px auto",
							border: "3px solid #1e2832",
						}}
					>
						<div style={{ fontSize: "32px", color: "#cc0000" }}>🔒</div>
					</div>

					<Typography
						variant="h6"
						style={{
							marginBottom: "16px",
							fontFamily: "MAN EUROPE_Bold",
							color: "#333",
							fontSize: "20px",
						}}
					>
						Access Denied
					</Typography>

					<Typography
						variant="body1"
						style={{
							color: "#666",
							lineHeight: "1.6",
							fontSize: "16px",
							marginBottom: "8px",
						}}
					>
						You have <strong>read-only</strong> access and cannot make changes.
					</Typography>

					<Typography
						variant="body2"
						style={{ color: "#888", fontSize: "14px" }}
					>
						Please contact your administrator if you need write access.
					</Typography>
				</DialogContent>

				<DialogActions
					style={{ padding: "0 24px 24px 24px", justifyContent: "center" }}
				>
					<Button
						onClick={() => setShowAccessDeniedModal(false)}
						variant="contained"
						style={{
							backgroundColor: "#cc0000",
							color: "white",
							borderRadius: "5px",
							padding: "10px 32px",
							fontFamily: "MAN EUROPE_Bold",
							textTransform: "none",
							fontSize: "14px",
						}}
						onMouseOver={(e) => (e.target.style.backgroundColor = "#b30000")}
						onMouseOut={(e) => (e.target.style.backgroundColor = "#cc0000")}
					>
						OK
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
});

export default SerieTrackingDataTable;
